import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { BASE_URL } from "../../config/axios";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { formattedDate } from "../Date";
import { ToastContainer, toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import Docs from "../../assets/svg/docs.svg";
import Profile from "../../assets/svg/profile.svg";
import CreatableSelect from 'react-select/creatable';
import { AddwithBlueCircle, AddwithWhiteCircle, DeleteDustbin, DownloadIcon, DropdownArrow, DropdownArrowOption, ImportantNotesIcon, InfoIcon, TripledotLarge, UpdatePencil } from "../AllSvg";
import { getNumberSuffix, handleAllError, handleErrorToast, CustomImageModal, CustomTooltip } from "../CustomFunctions";
import { CSSForFilterDropdown, CSSForSelect, CSSForSelect10vWWidth, CSSForSelectAutoSize, CSSForSelectSmallWidth, CustomCSSForSearchableSelect } from "../CustomUtilities";
import Loader from "../Loader";
import UserDefaultLogo from "../UserDetails/UserDefaultLogo";
import ProfileUploadModal from "../UserDetails/userDetails-comp/UploadProfile";
import { generateCustomKey, generateCustomFileForUpload } from "../CustomFunctions";

const PersonaldetailsAdd = ({ email, id, forwardroutepath }) => {
  const [jdid, setJdid] = useState();
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    name: "",
    email: email,
    alternate_email: null,
    father_name: "",
    mother_name: "",
    mobile: "",
    alt_mobile: null,
    gender: "",
    dob: "",
    blood_group: "",
    birth_mark: "",
    place_of_birth: "",
    married_status: "",
    marriage_date: null,
    aadhaar: "",
    pan: "",
    uan: "",
    esi: "",
    permanent_address: "",
    current_address: "",
    current_permanent_same: false,
  });

  const [jobApplicant, setJobApplicant] = useState({
    name: "",
    jobpostdetails: "",
    applied_date: "",
    user_detail: "",
  });

  // !  ************** Validation start **************  ! //
  // Add this validation function for Date of Birth
  const validateDOB = (dob) => {
    if (!dob) return false;

    const selectedDate = new Date(dob);
    const currentDate = new Date();

    // Check if date is valid
    if (isNaN(selectedDate.getTime())) return false;

    // Check if date is in the future
    if (selectedDate > currentDate) return false;

    // Optional: Check if person is at least 15 years old (for employment)
    const minAgeDate = new Date();
    minAgeDate.setFullYear(minAgeDate.getFullYear() - 15);
    if (selectedDate > minAgeDate) {
      // This is just a warning, not a hard validation - you can decide if you want to block
      return true; // Still return true for valid format, but you can show a warning
    }

    return true;
  };

  const validateMarriageDate = (marriageDate, maritalStatus, dob) => {
    if (!marriageDate) return false;

    const selectedDate = new Date(marriageDate);
    const currentDate = new Date();
    const dobDate = new Date(dob);

    // Check if date is valid
    if (isNaN(selectedDate.getTime())) return false;

    // Check if date is in the future
    if (selectedDate > currentDate) return false;

    // Check if marriage date is after DOB
    if (dob && selectedDate < dobDate) return false;

    return true;
  };

  const validateName = (name) => {
    if (!name) return false;
    // Allow letters, spaces, hyphens, and apostrophes
    const nameRegex = /^[A-Za-z\s\-']+$/;
    return nameRegex.test(name) && name.length <= 50;
  };

  // Validation helper functions
  const validateAadhaar = (aadhaar) => {
    if (!aadhaar) return false;

    // Convert to string if it's a number
    const aadhaarStr = String(aadhaar);

    // Remove spaces and hyphens for validation
    const cleanAadhaar = aadhaarStr.replace(/[\s-]/g, '');

    // Check if it's exactly 12 digits and doesn't start with 0 or 1
    const aadhaarRegex = /^[2-9][0-9]{11}$/;
    return aadhaarRegex.test(cleanAadhaar);
  };

  const validatePhoneNumber = (phone) => {
    if (!phone && phone !== 0) return false;

    // Convert to string if it's a number
    const phoneStr = String(phone);

    // Remove any non-digit characters
    const cleanPhone = phoneStr.replace(/\D/g, '');

    // Check if it's exactly 10 digits and starts with 6-9 (Indian mobile number validation)
    const phoneRegex = /^[6-9][0-9]{9}$/;
    return phoneRegex.test(cleanPhone);
  };

  const validatePAN = (pan) => {
    if (!pan) return false;

    // Convert to string if it's a number
    const panStr = String(pan);

    // Remove spaces and convert to uppercase
    const cleanPan = panStr.replace(/\s/g, '').toUpperCase();

    // More specific PAN format with 4th character validation (P,C,H,F,A,T,B,L,J,G)
    // [A-Z]{3} - First three letters
    // [PCHFATBLJG]{1} - Fourth character (entity type)
    // [A-Z]{1} - Fifth character (first letter of surname/name)
    // [0-9]{4} - Four digits
    // [A-Z]{1} - Last character
    const panRegex = /^[A-Z]{3}[PCHFATBLJG]{1}[A-Z]{1}[0-9]{4}[A-Z]{1}$/;

    return panRegex.test(cleanPan);
  };

  const validateUAN = (uan) => {
    if (!uan) return true; // Not required, so return true if empty

    // Convert to string if it's a number
    const uanStr = String(uan);

    // UAN should be 12 digits
    const cleanUan = uanStr?.replace(/\D/g, '');
    const uanRegex = /^[0-9]{12}$/;
    return uanRegex.test(cleanUan);
  };

  const validateESI = (esi) => {
    if (!esi) return true; // Not required, so return true if empty

    // Convert to string if it's a number
    const esiStr = String(esi);

    // ESI format: XX-XXXXXXXXX-XXX (2 digits - 9 digits - 3 digits)
    const cleanEsi = esiStr?.replace(/[\s-]/g, '');
    const digitRegex = /^[0-9]{14}$/; // 14 digits total
    if (!digitRegex.test(cleanEsi)) return false;
    return digitRegex.test(cleanEsi);
  };

  const [errors, setErrors] = useState({});
  const [inputState, setInputState] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      "name",
      "email",
      "father_name",
      "mother_name",
      "mobile",
      "gender",
      "dob",
      "blood_group",
      // "birth_mark",
      "married_status",
      "aadhaar",
      "pan",
      "current_address",
      "permanent_address",
    ].filter(Boolean);

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)} is required !`;
      }
    });

    // Additional validations

    // Name validation
    if (formData.name && !validateName(formData.name)) {
      newErrors.name = "Name should contain only letters, spaces, and hyphens!";
    }

    if (formData.father_name && !validateName(formData.father_name)) {
      newErrors.father_name = "Father's name should contain only letters, spaces, and hyphens!";
    }

    if (formData.mother_name && !validateName(formData.mother_name)) {
      newErrors.mother_name = "Mother's name should contain only letters, spaces, and hyphens!";
    }

    // Mobile validations
    if (formData.mobile && !validatePhoneNumber(formData.mobile)) {
      newErrors.mobile = "Please enter a valid 10-digit mobile number starting with 6-9!";
    }

    if (formData.alt_mobile && !validatePhoneNumber(formData.alt_mobile)) {
      newErrors.alt_mobile = "Please enter a valid 10-digit mobile number starting with 6-9!";
    }

    // DOB validation - NON-FUTURE DATE
    if (formData.dob) {
      if (!validateDOB(formData.dob)) {
        newErrors.dob = "Please enter a valid date of birth (cannot be in the future)!";
      } else {
        // Optional: Add age warning if under 15
        const selectedDate = new Date(formData.dob);
        const minAgeDate = new Date();
        minAgeDate.setFullYear(minAgeDate.getFullYear() - 15);

        if (selectedDate > minAgeDate) {
          // This is a warning, not an error - you can show this in a different way
          console.warn("Candidate is under 15 years old");
          // You could set a warning state here if you want to display it
          // setAgeWarning("Candidate is under 15 years old. Please verify eligibility.");
        }
      }
    }

    // Aadhaar validation
    if (formData.aadhaar && !validateAadhaar(formData.aadhaar)) {
      newErrors.aadhaar = "Please enter a valid 12-digit Aadhaar number (should not start with 0 or 1)!";
    }

    // PAN validation
    if (formData.pan && !validatePAN(formData.pan)) {
      newErrors.pan = "Please enter a valid PAN (Format: AAAAA1234A)!";
    }

    // UAN validation (if provided)
    if (formData.uan && !validateUAN(formData.uan)) {
      newErrors.uan = "Please enter a valid 12-digit UAN number!";
    }

    // ESI validation (if provided)
    if (formData.esi && !validateESI(formData.esi)) {
      newErrors.esi = "Please enter a valid ESI number (Format: XX-XXXXXXXXX-XXX)!";
    }

    // Marriage date validation
    const marriedStatuses = ["Married", "Divorced", "Widowed", "Separated"];
    if (marriedStatuses.includes(formData.married_status)) {
      if (!formData.marriage_date) {
        newErrors.marriage_date = "Marriage date is required for Married, Divorced, Widowed, or Separated status!";
      } else if (!validateMarriageDate(formData.marriage_date, formData.married_status, formData.dob)) {
        newErrors.marriage_date = "Please enter a valid marriage date (cannot be future or before your birth)!";
      }
    }

    setErrors(newErrors);
    setIsSubmitted(true);

    return Object.keys(newErrors).length === 0;
  };

  const tooltipContent = {
    aadhaar: (
      <div className="tooltip-options-whitebg">
        <h4 className="">Aadhaar Validation Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Must be exactly 12 digits</li>
          <li>Cannot start with 0 or 1</li>
          <li>Format: XXXXYYYYZZZZ (no spaces)</li>
          <li className="example">Example: 432143214321</li>
          <li className="note">Note: Spaces are for display only</li>
        </ul>
      </div>
    ),

    mobile: (
      <div className="tooltip-options-whitebg">
        <h4 className="">Mobile Number Validation Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Must be exactly 10 digits</li>
          <li>Must start with 6, 7, 8, or 9</li>
          <li>Only Indian mobile numbers allowed</li>
          <li className="example">Example: 9876543210</li>
          <li className="note">Note: No country code required</li>
        </ul>
      </div>
    ),

    alt_mobile: (
      <div className="tooltip-options-whitebg">
        <h4 className="">Alternate Mobile Number Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Must be exactly 10 digits (if provided)</li>
          <li>Must start with 6, 7, 8, or 9</li>
          <li>Optional field - can be left blank</li>
          <li className="example">Example: 9876543210</li>
          <li className="note">Note: Same validation as primary mobile</li>
        </ul>
      </div>
    ),

    pan: (
      <div className="tooltip-options-whitebg">
        <h4 className="">PAN Card Validation Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Format: AAAAA1234A</li>
          <li>First 5 characters: Letters (A-Z)</li>
          <li>Next 4 characters: Numbers (0-9)</li>
          <li>Last character: Letter (A-Z)</li>
          <li>4th character must be: P,C,H,F,A,T,B,L,J,G</li>
          <li className="example">Example: ABCDE1234F</li>
          <li className="note">Note: Automatically converts to uppercase</li>
        </ul>
      </div>
    ),

    uan: (
      <div className="tooltip-options-whitebg">
        <h4 className="">UAN (Universal Account Number) Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Must be exactly 12 digits (if provided)</li>
          <li>Only numbers allowed</li>
          <li>Optional field - can be left blank</li>
          <li className="example">Example: 123456789012</li>
          <li className="note">Note: Provided by EPFO</li>
        </ul>
      </div>
    ),

    esi: (
      <div className="tooltip-options-whitebg">
        <h4 className="">ESI Number Validation Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Format: XX-XXXXXXXXX-XXX</li>
          <li>2 digits - 9 digits - 3 digits</li>
          <li>Total 17 digits</li>
          <li>Hyphens added automatically</li>
          <li>Optional field - can be left blank</li>
          <li className="example">Example: 12-123456789-012</li>
          <li className="note">Note: Provided by ESIC</li>
        </ul>
      </div>
    ),

    email: (
      <div className="tooltip-options-whitebg">
        <h4 className="">Email Validation Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Must be valid email format</li>
          <li>Must contain @ and .domain</li>
          <li>Automatically converts to lowercase</li>
          <li className="example">Example: name@company.com</li>
          <li className="note">Note: Used for all communications</li>
        </ul>
      </div>
    ),

    name: (
      <div className="tooltip-options-whitebg">
        <h4 className="">Name Field Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Required field</li>
          <li>Maximum 50 characters</li>
          <li>Letters, spaces, and hyphens only</li>
          <li className="example">Example: John Doe</li>
          <li className="note">Note: As per official documents</li>
        </ul>
      </div>
    ),

    father_name: (
      <div className="tooltip-options-whitebg">
        <h4 className="">Father's Name Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Required field</li>
          <li>Maximum 50 characters</li>
          <li>Letters, spaces, and hyphens only</li>
          <li className="example">Example: Robert Johnson</li>
          <li className="note">Note: As per official documents</li>
        </ul>
      </div>
    ),

    mother_name: (
      <div className="tooltip-options-whitebg">
        <h4 className="">Mother's Name Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Required field</li>
          <li>Maximum 50 characters</li>
          <li>Letters, spaces, and hyphens only</li>
          <li className="example">Example: Sarah Johnson</li>
          <li className="note">Note: As per official documents</li>
        </ul>
      </div>
    ),

    dob: (
      <div className="tooltip-options-whitebg">
        <h4 className="">Date of Birth Validation Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Required field</li>
          <li>Cannot be a future date</li>
          <li>Must be a valid calendar date</li>
          <li>Should be at least 15 years old (for employment)</li>
          <li className="example">Example: 15/05/1995</li>
          <li className="note">Future dates are not accepted</li>
          <li className="note">Must match your official documents</li>
        </ul>
      </div>
    ),

    gender: (
      <div className="tooltip-options-whitebg">
        <h4 className="">Gender Selection</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Required field</li>
          <li>Select from available options</li>
          <li className="example">Options: Male, Female, Other</li>
          <li className="note">Note: As per your official records</li>
        </ul>
      </div>
    ),

    blood_group: (
      <div className="tooltip-options-whitebg">
        <h4 className="">Blood Group Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Required field</li>
          <li>Select from standard blood groups</li>
          <li>Option "Not Know" available</li>
          <li className="example">Options: A+, A-, B+, B-, AB+, AB-, O+, O-</li>
          <li className="note">Note: Important for medical emergencies</li>
        </ul>
      </div>
    ),

    married_status: (
      <div className="tooltip-options-whitebg">
        <h4 className="">Marital Status Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Required field</li>
          <li>Select one option</li>
          <li>Marriage date required for Married/Divorced/Widowed/Separated</li>
          <li className="example">Options: Single, Married, Divorced, Widowed, Separated</li>
          <li className="note">Marriage date mandatory for married status</li>
        </ul>
      </div>
    ),

    marriage_date: (
      <div className="tooltip-options-whitebg">
        <h4 className="">Marriage Date Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Required if Married/Divorced/Widowed/Separated</li>
          <li>Cannot be a future date</li>
          <li>Must be a valid calendar date</li>
          <li>Must be after your date of birth</li>
          <li className="example">Example: 10/12/2020</li>
          <li className="note">Required for verification purposes</li>
        </ul>
      </div>
    ),

    birth_mark: (
      <div className="tooltip-options-whitebg">
        <h4 className="">Birth Mark</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Optional field</li>
          <li>Maximum 50 characters</li>
          <li>Describe any identifying marks</li>
          <li className="example">Example: Mole on right cheek</li>
          <li className="note">Note: Used for identification</li>
        </ul>
      </div>
    ),

    place_of_birth: (
      <div className="tooltip-options-whitebg">
        <h4 className="">Place of Birth</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Optional field</li>
          <li>Maximum 50 characters</li>
          <li>Enter city/town/district</li>
          <li className="example">Example: Mumbai, Maharashtra</li>
          <li className="note">Note: As per birth certificate</li>
        </ul>
      </div>
    ),

    current_address: (
      <div className="tooltip-options-whitebg">
        <h4 className="">Current Address Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Required field</li>
          <li>Maximum 300 characters</li>
          <li>Include complete address with PIN code</li>
          <li className="example">Example: 123, Street Name, City - 400001</li>
          <li className="note">Used for all correspondence</li>
        </ul>
      </div>
    ),

    permanent_address: (
      <div className="tooltip-options-whitebg">
        <h4 className="">Permanent Address Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Required field</li>
          <li>Maximum 300 characters</li>
          <li>Include complete address with PIN code</li>
          <li>Can use "Current as Default Address" option</li>
          <li className="example">Example: 456, Main Road, City - 400001</li>
          <li className="note">As per official records</li>
        </ul>
      </div>
    )
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setInputState({
      ...inputState,
      [name]: value.trim() ? "green" : "",
    });

    setFormData({
      ...formData,
      [name]: value,
    });

    if (value.trim()) {
      setErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors[name];
        return updatedErrors;
      });
    }
  };

  const handleCustomInputChange = (e) => {
    const { name, value } = e.target;

    // Convert to string for processing
    const valueStr = String(value);
    let processedValue = valueStr;

    // Format Aadhaar: automatically add spaces after every 4 digits
    // if (name === 'aadhaar') {
    //   // Remove all non-digits
    //   const cleaned = valueStr.replace(/\D/g, '');

    //   // Format as XXXX XXXX XXXX
    //   if (cleaned.length <= 4) {
    //     processedValue = cleaned;
    //   } else if (cleaned.length <= 8) {
    //     processedValue = `${cleaned.slice(0, 4)} ${cleaned.slice(4)}`;
    //   } else {
    //     processedValue = `${cleaned.slice(0, 4)} ${cleaned.slice(4, 8)} ${cleaned.slice(8, 12)}`;
    //   }

    //   // Limit to 14 characters (12 digits + 2 spaces)
    //   if (processedValue.length > 14) {
    //     processedValue = processedValue.slice(0, 14);
    //   }
    // }

    // Format Aadhaar: just digits, 
    if (name === 'aadhaar') {
      // Remove all non-digits
      const cleaned = valueStr.replace(/\D/g, '');

      // Limit to 12 digits
      processedValue = cleaned.slice(0, 12);
    }

    // Format UAN: just digits, no formatting needed but ensure only digits
    if (name === 'uan') {
      processedValue = valueStr.replace(/\D/g, '').slice(0, 12);
    }

    // Format ESI: automatically add hyphens in format XX-XXXXXXXXX-XXX
    if (name === 'esi') {
      // Remove all non-digits
      const cleaned = valueStr.replace(/\D/g, '');

      // Format as XX-XXXXXXXXX-XXX
      if (cleaned.length <= 2) {
        processedValue = cleaned;
      } else if (cleaned.length <= 11) {
        processedValue = `${cleaned.slice(0, 2)}-${cleaned.slice(2)}`;
      } else {
        processedValue = `${cleaned.slice(0, 2)}-${cleaned.slice(2, 11)}-${cleaned.slice(11, 14)}`;
      }

      // Limit to 20 characters (17 digits + 2 hyphens)
      if (processedValue.length > 20) {
        processedValue = processedValue.slice(0, 20);
      }
    }

    setInputState({
      ...inputState,
      [name]: processedValue.trim() ? "green" : "",
    });

    setFormData({
      ...formData,
      [name]: processedValue,
    });

    if (processedValue.trim()) {
      setErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors[name];
        return updatedErrors;
      });
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: checked,
    }));

    // If checked, set permanent_address to current_address
    if (checked) {
      setFormData((prevData) => ({
        ...prevData,
        permanent_address: prevData.current_address,
      }));
    }

    if (!checked) {
      setFormData((prevData) => ({
        ...prevData,
        permanent_address: "",
      }));
    }

    // Remove permanent_address error when checkbox is checked
    if (checked) {
      setErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors.permanent_address;
        return updatedErrors;
      });

      // Also set input state for permanent_address to show success
      setInputState((prevState) => ({
        ...prevState,
        permanent_address: "green",
      }));
    } else {
      // When unchecked, clear the input state for permanent_address
      setInputState((prevState) => ({
        ...prevState,
        permanent_address: "",
      }));
    }
  };

  //same date

  // ?  ************** Validation End **************  ! //
  const navigate = useNavigate();


  const generatePassword = generateCustomKey('Aimantra');

  const [loading, setLoading] = useState(false); //loading logic

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setLoading(true); //loading logic
      const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic

      // Check if this is an existing user
      const isExistingUser = data ? true : false;

      const updatedJobApplicant = {
        ...formData,
        employee: formData.email,
        email: formData.email,
        alt_mobile: formData.alt_mobile || null,

        jobpostdetails: id,
        applied_date: formattedDate,
        user_detail: formData.email,
        user: formData.email,
        remarks: "Pending for Check",
        role: "Jobapplicant",
      };

      // Only include password for NEW users
      if (!isExistingUser) {
        updatedJobApplicant.password = generatePassword;
        // Also include name if it's a new user
        updatedJobApplicant.name = formData.name;
      } else {
        // For existing users, remove password field completely
        delete updatedJobApplicant.password;
      }

      try {
        let ress = await axios.post(
          `${BASE_URL}/jd/jobapplicant/`,
          updatedJobApplicant
        );

        if (ress.status === 200) {
          forwardroutepath === "educationDetailss" ?
            navigate(`/${forwardroutepath}/${formData.email}/?company_id=${sessionStorage.getItem("company_id") || "null"}`) : navigate(`/${forwardroutepath}/${id}/${formData.email}/`);
        } else {
          alert(ress);
        }
      } catch (err) {
        //toast Logic
        handleErrorToast(err, loadingToastId)
      } finally {
        setLoading(false); // Ensure loading state is stopped even in case of error //loading logic
        toast.dismiss(loadingToastId);
      }
    }
  };

  const handleFormPut = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setLoading(true); //loading logic
      const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic

      const { profilepic, signature, ...formDataWithoutProfileNSignture } = formData; // Destructure to remove profilepic

      const updatedFormData = {
        ...formDataWithoutProfileNSignture,
        employee: formData.email,
      };

      const newJobApplication = {
        ...formData,  // Include ALL form data
        employee: formData.email,
        email: formData.email,
        alt_mobile: formData.alt_mobile || null,
        jobpostdetails: id,
        applied_date: formattedDate,
        user_detail: formData.email,
        user: formData.email,
        remarks: "Pending for Check",
        role: "Jobapplicant",
      };
      // Remove password for existing users
      delete newJobApplication.password;
      delete newJobApplication.profilepic;
      delete newJobApplication.signature;

      const updatedJobApplicant = {
        ...jobApplicant,
        name: formData.name,
        jobpostdetails: id,
        applied_date: formattedDate,
        user_detail: formData.email,
      };

      try {
        let res = await axios.put(
          `${BASE_URL}/userdata/userdata/${formData.email}/`,
          updatedFormData
        );

        if (jdid !== id) {
          let jobAppRes = await axios.post(
            `${BASE_URL}/jd/jobapplicant/`,
            newJobApplication
          );
          if (jobAppRes.status === 200 || jobAppRes.status === 201) {
            console.log("New job application created for position:", id);
          }
        }

        if (res.status === 200) {
          forwardroutepath === "educationDetailss" ?
            navigate(`/${forwardroutepath}/${formData.email}/?company_id=${sessionStorage.getItem("company_id") || "null"}`) : navigate(`/${forwardroutepath}/${id}/${formData.email}/`);
        } else {
          alert(res);
        }
      } catch (err) {
        handleErrorToast(err, loadingToastId)
      } finally {
        setLoading(false); //loading logic
        toast.dismiss(loadingToastId);
      }
    }
  };

  const [data, setData] = useState(false);
  const [jobposition, setJobPosition] = useState({});
  const [buffer1, setBuffering1] = useState(false); //buffering logic
  const [buffer2, setBuffering2] = useState(false); //buffering logic
  const [buffer3, setBuffering3] = useState(false); //buffering logic

  const getUserDetails = async () => {
    setBuffering1(true); //buffering logic
    try {
      const res = await axios.get(
        `${BASE_URL}/userdata/userdataget/${formData.email}/`
      );
      setData(true);
      // setFormData(res.data);
      setFormData({
        ...res.data,
        current_permanent_same: res.data?.current_address === res.data?.permanent_address
      });

    } catch (err) {
      setData(false);
      handleErrorToast(err)
    } finally {
      setBuffering1(false);// End Buffering
    }
  };

  // const getJobApplicationDetails = async () => {
  //   setBuffering2(true); //buffering logic // Start Buffering

  //   try {
  //     const ress = await axios.get(
  //       `${BASE_URL}/jd/jobapplicant/${id}/${formData.email}/`
  //     );
  //     setJdid(ress.data.jobpostdetails);
  //   } catch (err) {
  //     handleErrorToast(err)
  //   } finally {
  //     setBuffering2(false);// End Buffering
  //   }
  // };

  // const getJobApplicationDetails = async () => {
  //   setBuffering2(true);

  //   try {
  //     const url = id
  //       ? `${BASE_URL}/jd/jobapplicant/${id}/${formData.email}/`
  //       : `${BASE_URL}/jd/jobapplicant/null/${formData.email}/`;

  //     const ress = await axios.get(url);
  //     setJdid(ress.data.jobpostdetails);
  //   } catch (err) {
  //     handleErrorToast(err);
  //   } finally {
  //     setBuffering2(false);
  //   }
  // };
  const getJobApplicationDetails = async () => {
    if (!id) return;
    setBuffering2(true); //buffering logic // Start Buffering

    try {
      const ress = await axios.get(
        `${BASE_URL}/jd/jobapplicant/${id}/${formData.email}/`
      );
      setJdid(ress.data.jobpostdetails);
    } catch (err) {
      handleErrorToast(err)
    } finally {
      setBuffering2(false);// End Buffering
    }
  };

  // const getJobPositionDetails = async () => {
  //   setBuffering3(true); //buffering logic // Start Buffering
  //   try {
  //     const res = await axios.get(`${BASE_URL}/jd/jobpost/${id}/`);
  //     setJobPosition(res.data);
  //   } catch (err) {
  //     const message = err.response?.data?.message || err.message;
  //     toast.error(message);
  //     // handleErrorToast(err)
  //   } finally {
  //     setBuffering3(false);// End Buffering
  //   }
  // };

  // const getJobPositionDetails = async () => {
  //   setBuffering3(true); // Start Buffering

  //   try {
  //     const url = id
  //       ? `${BASE_URL}/jd/jobpost/${id}/`
  //       : `${BASE_URL}/jd/jobpost/`;

  //     const res = await axios.get(url);
  //     setJobPosition(res.data);
  //   } catch (err) {
  //     const message = err.response?.data?.message || err.message;
  //     toast.error(message);
  //     // handleErrorToast(err)
  //   } finally {
  //     setBuffering3(false); // End Buffering
  //   }
  // };
  const getJobPositionDetails = async () => {
    if (!id) return;
    setBuffering3(true); //buffering logic // Start Buffering
    try {
      const res = await axios.get(`${BASE_URL}/jd/jobpost/${id}/`);
      setJobPosition(res.data);
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      toast.error(message);
      // handleErrorToast(err)
    } finally {
      setBuffering3(false);// End Buffering
    }
  };
  useEffect(() => {
    if (id) {
      getJobApplicationDetails();
      getJobPositionDetails();
    }
    getUserDetails();
  }, [formData.email, email, id, jdid]);

  useEffect(() => {
    const companyIdFromUrl = searchParams.get("company_id");
    if (companyIdFromUrl && companyIdFromUrl !== "null") {
      sessionStorage.setItem("company_id", companyIdFromUrl);
    }
  }, [searchParams]);

  return (
    <>
      {buffer1 ? <Loader /> : null}{/* //buffering logic */}
      <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
      <div className="jd-heading-outer">
        <h2 className="jd-heading-main font-weight600  font-size-heading">Personal Information*</h2>
        <div>
          <div className="jd-heading-bottom-bold"></div>
          <div className="jd-heading-bottom-light"></div>
        </div>
      </div>

      <form className="register-form">
        <div className="form-flex-wrap">

          {/* Email Field */}
          <div className="flex-column width-flex-50">
            <label htmlFor="email" className="form-labels font-weight500    font-size-subheading">
              <CustomTooltip position="top" tooltipContent={tooltipContent.email}>
                Email<span className="required">*</span>
                {/* <InfoIcon /> */}
              </CustomTooltip>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="Demo@gmail.com"
              autoComplete="off"
              onBlur={handleInputChange}
              onInput={(e) => {
                e.target.value = e.target.value.toLowerCase().trim(); // Convert input to lowercase
              }}
              // value={formData.email}
              defaultValue={formData.email}
              className={`form-input width-respo ${errors.email ? "error" : inputState.email ? "success" : ""
                }`}
            />
            {errors.email && (
              <span className="error-message font-size-text ">{errors.email}</span>
            )}
          </div>

          {/* Name Field */}
          <div className="flex-column width-flex-50">
            <label htmlFor="name" className="form-labels font-weight500    font-size-subheading">
              <CustomTooltip position="top" tooltipContent={tooltipContent.name}>
                Name<span className="required">*</span>
                {/* <InfoIcon /> */}
              </CustomTooltip>
            </label>

            <input
              type="text"
              id="name"
              name="name"
              placeholder="Candidate name"
              maxLength={50}
              // onChange={handleInputChange}
              onChange={(e) => {
                const value = e.target.value.replace(/[^A-Za-z\s\-']/g, "").slice(0, 50);
                handleInputChange({
                  target: { name: "name", value }
                });
              }}
              value={formData.name}
              className={`form-input width-respo text-capitalize ${errors.name ? "error" : inputState.name ? "success" : ""
                }`}
            />
            {errors.name && (
              <span className="error-message font-size-text ">{errors.name}</span>
            )}
          </div>

          {/* Alternate Email */}
          <div className="flex-column width-flex-50">
            <label htmlFor="alternate_email" className="form-labels font-weight500    font-size-subheading">
              <CustomTooltip position="top" tooltipContent={tooltipContent.alternate_email}>
                Alternate Email
                {/* <InfoIcon /> */}
              </CustomTooltip>
            </label>
            <input
              type="alternate_email"
              id="alternate_email"
              name="alternate_email"
              placeholder="Demo@gmail.com"
              autoComplete="off"
              onChange={handleInputChange}
              onInput={(e) => {
                e.target.value = e.target.value.toLowerCase().trim(); // Convert input to lowercase
              }}
              value={formData.alternate_email}
              className={`form-input width-respo ${errors.alternate_email
                ? "error"
                : inputState.alternate_email
                  ? "success"
                  : ""
                }`}
            />
          </div>

          {/* DOB Field */}
          <div className="flex-column width-flex-50">
            <label htmlFor="dob" className="form-labels font-weight500    font-size-subheading">
              <CustomTooltip position="top" tooltipContent={tooltipContent.dob}>
                Date of Birth<span className="required">*</span>
                {/* <InfoIcon /> */}
              </CustomTooltip>
            </label>
            <input
              type="date"
              id="dob"
              name="dob"
              // onChange={handleInputChange}
              onChange={(e) => {
                const value = e.target.value;
                const date = new Date(value);
                const today = new Date();
                if (value && (isNaN(date.getTime()) || date > today)) {
                  return; // ignore invalid input
                }

                handleInputChange(e);
              }}
              max={new Date().toISOString().split("T")[0]} // prevents future date
              value={formData.dob}
              className={`form-input width-respo ${errors.dob ? "error" : inputState.dob ? "success" : ""
                }`}
            />
            {errors.dob && <span className="error-message font-size-text ">{errors.dob}</span>}
          </div>


          {/* Father Name Field */}
          <div className="flex-column width-flex-50">
            <label htmlFor="father_name" className="form-labels font-weight500    font-size-subheading">
              <CustomTooltip position="top" tooltipContent={tooltipContent.father_name}>
                Father Name<span className="required">*</span>
                {/* <InfoIcon /> */}
              </CustomTooltip>
            </label>
            <input
              maxLength={50}
              type="text"
              id="father_name"
              name="father_name"
              placeholder="Father name"
              // onChange={handleInputChange}
              onChange={(e) => {
                const value = e.target.value.replace(/[^A-Za-z\s\-']/g, "").slice(0, 50);
                handleInputChange({
                  target: { name: "father_name", value }
                });
              }}
              value={formData.father_name}
              className={`text-capitalize form-input width-respo ${errors.father_name
                ? "error"
                : inputState.father_name
                  ? "success"
                  : ""
                }`}
            />
            {errors.father_name && (
              <span className="error-message font-size-text ">{errors.father_name}</span>
            )}
          </div>

          {/* Mother Name Field */}
          <div className="flex-column width-flex-50">
            <label htmlFor="mother_name" className="form-labels font-weight500    font-size-subheading">
              <CustomTooltip position="top" tooltipContent={tooltipContent.mother_name}>
                Mother Name<span className="required">*</span>
                {/* <InfoIcon /> */}
              </CustomTooltip>
            </label>
            <input
              type="text"
              id="mother_name"
              maxLength={50}
              name="mother_name"
              placeholder="Mother name"
              // onChange={handleInputChange}
              onChange={(e) => {
                const value = e.target.value.replace(/[^A-Za-z\s\-']/g, "").slice(0, 50);
                handleInputChange({
                  target: { name: "mother_name", value }
                });
              }}
              value={formData.mother_name}
              className={`text-capitalize form-input width-respo ${errors.mother_name
                ? "error"
                : inputState.mother_name
                  ? "success"
                  : ""
                }`}
            />
            {errors.mother_name && (
              <span className="error-message font-size-text ">{errors.mother_name}</span>
            )}
          </div>

          {/* Mobile Field */}
          <div className="flex-column width-flex-50">
            <label htmlFor="mobile" className="form-labels font-weight500    font-size-subheading">
              <CustomTooltip position="top" tooltipContent={tooltipContent.mobile}>
                Mobile<span className="required">*</span>
                {/* <InfoIcon /> */}
              </CustomTooltip>
            </label>
            <input
              type="text"
              id="mobile"
              name="mobile"
              placeholder="9999988888"
              maxLength={10}
              inputMode="numeric"
              pattern="[0-9]*"
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 10);

                handleInputChange({
                  target: { name: "mobile", value }
                });
              }}
              value={formData.mobile}
              className={`form-input width-respo ${errors.mobile ? "error" : inputState.mobile ? "success" : ""
                }`}
            />
            {errors.mobile && (
              <span className="error-message font-size-text ">{errors.mobile}</span>
            )}
          </div>

          {/* Alternate Mobile Field */}
          <div className="flex-column width-flex-50">
            <label htmlFor="alt_mobile" className="form-labels font-weight500    font-size-subheading">
              <CustomTooltip position="top" tooltipContent={tooltipContent.alt_mobile}>
                Alternate Mobile
                {/* <InfoIcon /> */}
              </CustomTooltip>
            </label>
            <input
              type="text"
              id="alt_mobile"
              name="alt_mobile"
              placeholder="9999988888"
              maxLength={10}
              inputMode="numeric"
              pattern="[0-9]*"
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 10);

                handleInputChange({
                  target: { name: "alt_mobile", value }
                });
              }}
              value={formData.alt_mobile}
              className={`form-input width-respo ${errors.alt_mobile ? "error" : inputState.alt_mobile ? "success" : ""
                }`}
            />
            {errors.alt_mobile && (
              <span className="error-message font-size-text ">{errors.alt_mobile}</span>
            )}
          </div>

          {/* Gender Field */}
          <div className="flex-column width-flex-50">
            <label htmlFor="gender" className="form-labels font-weight500    font-size-subheading">
              <CustomTooltip position="top" tooltipContent={tooltipContent.gender}>
                Gender<span className="required">*</span>
                {/* <InfoIcon /> */}
              </CustomTooltip>
            </label>
            <select
              id="gender"
              name="gender"
              value={formData.gender}
              onChange={handleInputChange}
              className={`form-input width-respo ${errors.gender ? "error" : inputState.gender ? "success" : ""
                }`}
            >
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
            {errors.gender && (
              <span className="error-message font-size-text ">{errors.gender}</span>
            )}
          </div>

          {/* Blood Group Field */}
          <div className="flex-column width-flex-50">
            <label htmlFor="blood_group" className="form-labels font-weight500    font-size-subheading">
              <CustomTooltip position="top" tooltipContent={tooltipContent.blood_group}>
                Blood Group<span className="required">*</span>
                {/* <InfoIcon /> */}
              </CustomTooltip>
            </label>
            <select
              id="blood_group"
              name="blood_group"
              value={formData.blood_group}
              onChange={handleInputChange}
              className={`form-input width-respo ${errors.blood_group
                ? "error"
                : inputState.blood_group
                  ? "success"
                  : ""
                }`}
            >
              <option value="">Select a Blood Group</option>
              <option value="A+">A+</option>
              <option value="A-">A-</option>
              <option value="B+">B+</option>
              <option value="B-">B-</option>
              <option value="AB+">AB+</option>
              <option value="AB-">AB-</option>
              <option value="O+">O+</option>
              <option value="O-">O-</option>
              <option value="na">Not Know</option>
            </select>
            {errors.blood_group && (
              <span className="error-message font-size-text ">{errors.blood_group}</span>
            )}
          </div>

          {/* Birth Mark Field */}
          <div className="flex-column width-flex-50">
            <label htmlFor="birth_mark" className="form-labels font-weight500    font-size-subheading">
              <CustomTooltip position="top" tooltipContent={tooltipContent.birth_mark}>
                Birth Mark
                {/* <span className="required">*</span> */}
                {/* <InfoIcon /> */}
              </CustomTooltip>
            </label>
            <input
              type="text"
              id="birth_mark"
              name="birth_mark"
              placeholder="Birth mark"
              maxLength={50}
              onChange={handleInputChange}
              value={formData.birth_mark}
              className={`form-input width-respo ${errors.birth_mark
                ? "error"
                : inputState.birth_mark
                  ? "success"
                  : ""
                }`}
            />
            {errors.birth_mark && (
              <span className="error-message font-size-text ">{errors.birth_mark}</span>
            )}
          </div>

          {/* Place of Birth Field */}
          <div className="flex-column width-flex-50">
            <label htmlFor="place_of_birth" className="form-labels font-weight500    font-size-subheading">
              <CustomTooltip position="top" tooltipContent={tooltipContent.place_of_birth}>
                Birth Place
                {/* <InfoIcon /> */}
              </CustomTooltip>
            </label>
            <input
              type="text"
              id="place_of_birth"
              name="place_of_birth"
              placeholder="Place of birth"
              maxLength={50}
              onChange={handleInputChange}
              value={formData.place_of_birth}
              className={`form-input width-respo ${errors.place_of_birth
                ? "error"
                : inputState.place_of_birth
                  ? "success"
                  : ""
                }`}
            />
            {errors.place_of_birth && (
              <span className="error-message font-size-text ">{errors.place_of_birth}</span>
            )}
          </div>

          {/* Marital Status Field */}
          <div className="flex-column width-flex-50">
            <label htmlFor="married_status" className="form-labels font-weight500    font-size-subheading">
              <CustomTooltip position="top" tooltipContent={tooltipContent.married_status}>
                Marital Status<span className="required">*</span>
                {/* <InfoIcon /> */}
              </CustomTooltip>
            </label>
            <select
              id="married_status"
              name="married_status"
              value={formData.married_status}
              onChange={handleInputChange}
              className={`form-input width-respo ${errors.married_status
                ? "error"
                : inputState.married_status
                  ? "success"
                  : ""
                }`}
            >
              <option value="">Select a Married Status</option>
              <option value="Single">Single</option>
              <option value="Married">Married</option>
              <option value="Divorced">Divorced</option>
              <option value="Widowed">Widowed</option>
              <option value="Separated">Separated</option>
            </select>
            {errors.married_status && (
              <span className="error-message font-size-text ">{errors.married_status}</span>
            )}
          </div>

          {/* Marriage Date Field */}
          {
            ["Married", "Divorced", "Widowed", "Separated"].includes(formData.married_status) &&
            <div className="flex-column width-flex-50">
              <label htmlFor="marriage_date" className="form-labels font-weight500    font-size-subheading">
                <CustomTooltip position="top" tooltipContent={tooltipContent.marriage_date}>
                  Marriage Date
                  {["Married", "Divorced", "Widowed", "Separated"].includes(formData.married_status) &&
                    <span className="required">*</span>
                  }
                  {/* <InfoIcon /> */}
                </CustomTooltip>
              </label>
              <input
                type="date"
                id="marriage_date"
                name="marriage_date"
                placeholder="Demo Demo"
                onChange={handleInputChange}
                value={formData.marriage_date}
                className={`form-input width-respo ${errors.marriage_date
                  ? "error"
                  : inputState.marriage_date
                    ? "success"
                    : ""
                  }`}
              />
              {errors.marriage_date && (
                <span className="error-message font-size-text ">{errors.marriage_date}</span>
              )}
            </div>}

          {/* Aadhaar Field */}
          <div className="flex-column width-flex-50">
            <label htmlFor="aadhaar" className="form-labels font-weight500    font-size-subheading">
              <CustomTooltip position="top" tooltipContent={tooltipContent.aadhaar} >
                Aadhaar<span className="required">*</span>
                <InfoIcon />
              </CustomTooltip>
            </label>
            <input
              type="text"
              id="aadhaar"
              name="aadhaar"
              placeholder="432143214321"
              maxLength={12} // 12 digits
              onChange={handleCustomInputChange}
              value={formData.aadhaar}
              className={`form-input width-respo ${errors.aadhaar ? "error" : inputState.aadhaar ? "success" : ""
                }`}
            />
            {errors.aadhaar && (
              <span className="error-message font-size-text ">{errors.aadhaar}</span>
            )}
          </div>

          {/* PAN Field */}
          <div className="flex-column width-flex-50">
            <label htmlFor="pan" className="form-labels font-weight500    font-size-subheading">
              <CustomTooltip position="top" tooltipContent={tooltipContent.pan}>
                PAN<span className="required">*</span>
                <InfoIcon />
              </CustomTooltip>
            </label>
            <input
              type="text"
              id="pan"
              name="pan"
              placeholder="AAAAA1234A"
              min="0"
              maxLength={10}
              onChange={handleCustomInputChange}
              onInput={(e) => {
                e.target.value = e.target.value.toUpperCase(); // Convert input to uppercase
              }}
              value={formData.pan}
              className={`form-input width-respo ${errors.pan ? "error" : inputState.pan ? "success" : ""
                }`}
            />
            {errors?.pan && <span className="error-message font-size-text ">{errors.pan}</span>}
          </div>

          {/* UAN Field */}
          <div className="flex-column width-flex-50">
            <label htmlFor="uan" className="form-labels font-weight500    font-size-subheading">
              <CustomTooltip position="top" tooltipContent={tooltipContent.uan}>
                Uan (Optional)
                <InfoIcon />
              </CustomTooltip>
            </label>
            <input
              type="text"
              maxLength={12}
              id="uan"
              name="uan"
              placeholder="12-digit UAN number"
              onChange={handleCustomInputChange}
              value={formData.uan}
              className={`form-input width-respo ${errors.uan ? "error" : inputState.uan ? "success" : ""
                }`}

            />
            {errors.uan && <span className="error-message font-size-text">{errors.uan}</span>}
          </div>

          {/* ESI Field */}
          <div className="flex-column width-flex-50">
            <label htmlFor="esi" className="form-labels font-weight500    font-size-subheading">
              <CustomTooltip position="top" tooltipContent={tooltipContent.esi}>
                ESI (Optional)
                <InfoIcon />
              </CustomTooltip>
            </label>
            <input
              type="text"
              id="esi"
              maxLength={20}
              name="esi"
              placeholder="XX-XXXXXXXXX-XXX"
              onChange={handleCustomInputChange}
              value={formData.esi}
              className={`form-input width-respo ${errors.esi ? "error" : inputState.esi ? "success" : ""
                }`}
            />
            {errors.esi && <span className="error-message font-size-text">{errors.esi}</span>}
          </div>

          {/* Current Address Field */}
          <div className="flex-column width-flex-50">
            <label htmlFor="current_address" className="form-labels font-weight500    font-size-subheading">
              <CustomTooltip position="top" tooltipContent={tooltipContent.current_address}>
                Current Address<span className="required">*</span>
                {/* <InfoIcon /> */}
              </CustomTooltip>
            </label>
            <textarea
              type="text"
              id="current_address"
              name="current_address"
              maxLength={300}
              placeholder="Current Address"
              onChange={handleInputChange}
              value={formData.current_address}
              className={`form-input-textarea width-respo  font-weight400  font-size-subheading ${errors.current_address
                ? "error"
                : inputState.current_address
                  ? "success"
                  : ""
                }`}
            />
            {errors.current_address && (
              <span className="error-message font-size-text ">{errors.current_address}</span>
            )}
          </div>

          {/* Permanent Address Field */}
          <div className="flex-column width-flex-50">
            <label htmlFor="current_permanent_same" className="form-labels font-weight500 flex-row   font-size-subheading">
              <CustomTooltip position="top" tooltipContent={tooltipContent.permanent_address}>
                Permanent Address <span className="required">*</span>
                <span className="label-sub-heading   font-weight400  font-size-label"> (Use Current as Default Address)</span>
              </CustomTooltip>
              <hr className="field-cont-hr" />
              <label className="checkbox-click">
                <input
                  type="checkbox"
                  id="current_permanent_same"
                  maxLength={300}
                  name="current_permanent_same"
                  checked={formData.current_permanent_same}
                  onChange={handleCheckboxChange}
                />
                <div className="checkmark-small "></div>
              </label>
            </label>

            <textarea
              type="text"
              id="permanent_address"
              name="permanent_address"
              placeholder="Permanent Address"
              maxLength={300}
              onChange={handleInputChange}
              value={formData.permanent_address}
              className={`form-input-textarea width-respo  font-weight400  font-size-subheading ${errors.permanent_address
                ? "error"
                : inputState.permanent_address
                  ? "success"
                  : ""
                }`}
            />
            {errors.permanent_address && (
              <span className="error-message font-size-text ">{errors.permanent_address}</span>
            )}
          </div>
        </div>

        <div className="button-models">
          {data
            ?
            (
              buffer2
                ?
                <div className="spinner"></div>
                :
                <button
                  onClick={handleFormPut}
                  className="model-button  font-weight500    model-button-submit"
                  disabled={loading}
                >
                  {/* //loading logic */}
                  Next
                </button>

            )
            :
            jobposition.status === "active" ?
              buffer2
                ?
                <div className="spinner"></div>
                :
                (
                  <button
                    onClick={handleFormSubmit}
                    className="model-button  font-weight500    model-button-submit"
                    disabled={loading}
                  >
                    {/* //loading logic */}
                    Submit & Proceed
                  </button>
                ) : (
                <button
                  onClick={() => {
                    const notification = document.createElement("div");
                    notification.classList.add("notification");
                    notification.textContent =
                      "This Form is Freezed Please Contact Our HR Team";
                    document.body.appendChild(notification);
                    setTimeout(() => {
                      document.body.removeChild(notification);
                    }, 20000); // Remove notification after 2 seconds
                  }}
                  className="model-button  font-weight500    model-button-cancel font-size-heading"
                  disabled={loading}
                >
                  {/* //loading logic */}
                  Freezed
                </button>
              )}
        </div>
        <p className="error-message font-size-text">
          {isSubmitted && Object.keys(errors).length > 0 && (
            Object.keys(errors).length > 5 ? (
              <h5 className="text-center">Please fill all mandatory fields!</h5>
            ) : (
              <h6 className="text-center">
                {Object.keys(errors).map((field) =>
                  field.charAt(0).toUpperCase() + field.slice(1)
                ).join(', ') + ' are required!'}
              </h6>
            )
          )}
        </p>
      </form>
    </>
  );
};


const EducationdetailsAdd = ({ email, id, forwardroutepath, backroutepath }) => {
  const [inputFields, setInputFields] = useState([
    {
      college: "",
      institution_type: "",
      department: "10th",
      stream: "",
      startDate: "",
      endDate: "",
      grade: "",
      otherDepartment: "",
      user: email,
    },
  ]);
  const [showHierarchyModal, setShowHierarchyModal] = useState(false);

  const [errors, setErrors] = useState({});
  const [inputState, setInputState] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Validation functions
  const validateCollege = (college) => {
    if (!college) return false;
    return college.length >= 2 && college.length <= 100;
  };

  const validateDates = (startDate, endDate) => {
    if (!startDate || !endDate) return { valid: false };

    const start = new Date(startDate);
    const end = new Date(endDate);
    const currentDate = new Date();

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return { valid: false, error: "Invalid date format" };
    }

    if (start > currentDate) {
      return { valid: false, error: "Start date cannot be in the future" };
    }

    // if (end > currentDate) {
    //   return { valid: false, error: "End date cannot be in the future" };
    // }

    if (start > end) {
      return { valid: false, error: "Start date must be before end date" };
    }

    // Check if duration is reasonable (max 10 years for a degree)
    const yearDiff = end.getFullYear() - start.getFullYear();
    if (yearDiff > 10) {
      return { valid: false, error: "Education duration too long (max 10 years)" };
    }

    return { valid: true };
  };

  const validateGrade = (grade) => {
    if (!grade) return false;
    // Allow percentages, CGPA, grades like A+, etc.
    const gradeRegex = /^[0-9]{1,3}(\.\d{1,2})?%?$|^[A-F][+-]?$|^[0-9](\.[0-9])?\/[0-9](\.[0-9])?$/i;
    return grade.length <= 30;
  };

  const validateStream = (stream) => {
    if (!stream) return false;
    return stream.length >= 2 && stream.length <= 100;
  };

  const validateForm = () => {
    const newErrors = {};

    const mandatorySchool = ["10th"];
    const seniorSecondaryWithMandatorySchool = ["12th"];

    const bachelorDegreesWithMandatorySchool = [
      "Diploma",
      "ITI",
    ];

    const bachelorDegreesWithMandatoryHighSchool = [
      "B.A. (Bachelor of Arts)",
      "B.Com. (Bachelor of Commerce)",
      "B.Tech. (Bachelor of Technology)",
      "B.E. (Bachelor of Engineering )",
      "B.Arch. (Bachelor of Architecture)",
      "Other",
    ];

    const masterDegreesWithMandatorySchool = [
      "M.Com. (Masters of Commerce)",
      "MBA (Master of Business Administration)",
      "M.Tech. (Master of Technology)",
      "M.E. (Master of Engineering )",
      "PhD",
    ];

    const requiredFields = [
      "college",
      "department",
      "stream",
      "institution_type",
      "startDate",
      "endDate",
      "grade",
    ].filter(Boolean);

    inputFields.forEach((field, index) => {

      // Basic validation for all fields
      requiredFields.forEach((reqField) => {
        if (!field[reqField]) {
          newErrors[`${reqField}_${index}`] = `${reqField.charAt(0).toUpperCase() + reqField.slice(1)
            } is required!`;
        }
      });

      // College validation
      if (field.college && !validateCollege(field.college)) {
        newErrors[`college_${index}`] = "College name must be between 2 and 100 characters!";
      }

      // // Stream validation
      // if (field.stream && !validateStream(field.stream)) {
      //   newErrors[`stream_${index}`] = "Stream must be between 2 and 100 characters!";
      // }

      // Grade validation
      if (field.grade && !validateGrade(field.grade)) {
        newErrors[`grade_${index}`] = "Please enter a valid grade/percentage!";
      }

      // Date validation
      if (field.startDate && field.endDate) {
        const dateValidation = validateDates(field.startDate, field.endDate);
        if (!dateValidation.valid) {
          if (dateValidation.error.includes("Start date")) {
            newErrors[`startDate_${index}`] = dateValidation.error;
          } else if (dateValidation.error.includes("End date")) {
            newErrors[`endDate_${index}`] = dateValidation.error;
          } else {
            newErrors[`endDate_${index}`] = dateValidation.error;
          }
        }
      }

      // Additional validation for "Other" option
      if (field.department === "Other" && !field.department) {
        newErrors[`department_${index}`] = "Please specify the other degree!";
      }

      if (field.department === "Other" && field.otherDepartment && field.otherDepartment.length < 2) {
        newErrors[`department_${index}`] = "Other degree must be at least 2 characters!";
      }

      // Update department with otherDepartment if valid
      if (field.department === "Other" && field.otherDepartment) {
        field.department = field.otherDepartment;
      }

      // Education hierarchy validation

      // Additional validation for specific 12th
      if (seniorSecondaryWithMandatorySchool.includes(field.department)) {
        const hasTenth = inputFields.some((f) => f.department === "10th");
        if (!hasTenth) {
          newErrors[`10th_missing_${index}`] =
            "Completion of 10th is required for 12th.";
        }
      }

      // Additional validation for specific bachelor degrees
      if (bachelorDegreesWithMandatoryHighSchool.includes(field.department)) {
        // Check if 10th and 12th records exist in the input fields
        const hasTenth = inputFields.some((f) => f.department === "10th");
        const hasTwelfth = inputFields.some((f) => f.department === "12th");
        const hasDiploma = inputFields.some((f) => f.department === "Diploma");
        const hasITI = inputFields.some((f) => f.department === "ITI");

        if (!hasTenth) {
          newErrors[`10th_missing_${index}`] =
            "Completion of 10th is required for selected bachelor degree.";
        }
        if (!hasDiploma && !hasITI) {
          if (!hasTwelfth) {
            newErrors[`12th_missing_${index}`] =
              "\nCompletion of 12th is required for selected bachelor degree.";
          }
        }
      }

      // Additional validation for specific bachelor degrees
      if (bachelorDegreesWithMandatorySchool.includes(field.department)) {
        // Check if 10th and 12th records exist in the input fields
        const hasTenth = inputFields.some((f) => f.department === "10th");
        if (!hasTenth) {
          newErrors[`10th_missing_${index}`] =
            "Completion of 10th is required for selected bachelor degree.";
        }
      }

      if (masterDegreesWithMandatorySchool.includes(field.department)) {
        const hasTenth = inputFields.some((f) => f.department === "10th");
        const hasTwelfth = inputFields.some((f) => f.department === "12th");
        const hasDiploma = inputFields.some((f) => f.department === "Diploma");
        const hasITI = inputFields.some((f) => f.department === "ITI");
        const hasBachelor = inputFields.some((f) =>
          bachelorDegreesWithMandatoryHighSchool.includes(f.department) ||
          bachelorDegreesWithMandatorySchool.includes(f.department)
        );

        if (!hasTenth) {
          newErrors[`10th_missing_${index}`] =
            "Completion of 10th is required for selected master degree.";
        }
        // if (!hasDiploma && !hasITI) {
        //   if (!hasTwelfth) {
        //     newErrors[`12th_missing_${index}`] =
        //       "Completion of 12th is required for selected bachelor degree.";
        //   }
        // }

        if (!hasDiploma && !hasITI && !hasTwelfth) {
          newErrors[`12th_missing_${index}`] = "\n12th/Diploma/ITI is required for this degree.";
        }

        if (!hasBachelor) {
          newErrors[`bachelor_missing_${index}`] = "\nBachelor's degree is required for Master's/PhD.";
        }
      }
    });

    setErrors(newErrors);
    setIsSubmitted(true);

    // const hierarchyError = Object.entries(newErrors).some(([key, value]) =>
    //   key.includes("_missing_") ||
    //   value?.toLowerCase().includes("required for") ||
    //   value?.toLowerCase().includes("required for selected")
    // );
    // if (hierarchyError) {
    //   setShowHierarchyModal(true);
    // }

    const hierarchyError = Object.keys(newErrors).some(key =>
      key.includes("_missing_")
    );

    if (hierarchyError) {
      setShowHierarchyModal(true);
    }

    return Object.keys(newErrors).length === 0;
  };

  // Tooltip content
  const tooltipContent = {
    college: (
      <div className="tooltip-options-whitebg">
        <h4>College/Institution Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Required field</li>
          <li>Minimum 2 characters</li>
          <li>Maximum 100 characters</li>
          <li className="example">Example: Indian Institute of Technology, Bombay</li>
          <li className="note">Enter full name of institution</li>
        </ul>
      </div>
    ),

    degreeType: (
      <div className="tooltip-options-whitebg">
        <h4>Degree Type Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Required field</li>
          <li>Select from available options</li>
          <li>Choose "Other" if your degree is not listed</li>
          <li className="example">Examples: 10th, 12th, B.Tech, MBA, PhD</li>
          <li className="note">Education hierarchy will be validated</li>
        </ul>
      </div>
    ),

    otherDegree: (
      <div className="tooltip-options-whitebg">
        <h4>Specify Other Degree</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Required if "Other" is selected</li>
          <li>Maximum 100 characters</li>
          <li className="example">Example: B.Sc. Computer Science</li>
          <li className="note">Enter your degree name clearly</li>
        </ul>
      </div>
    ),

    stream: (
      <div className="tooltip-options-whitebg">
        <h4>Stream/Specialization Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Required field</li>
          {/* <li>Minimum 2 characters</li> */}
          <li>Maximum 100 characters</li>
          <li>Can create new stream if not in list</li>
          <li className="example">Examples: Computer Science, Electronics, Finance</li>
          <li className="note">Select or type your specialization</li>
          <li className="note">In Case of 10th/12th give Subject names</li>
        </ul>
      </div>
    ),

    institutionType: (
      <div className="tooltip-options-whitebg">
        <h4>Institution Type Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Required field</li>
          <li>Select Government or Private</li>
          <li className="example">Government: Public funded institutions</li>
          <li className="example">Private: Self-financed institutions</li>
        </ul>
      </div>
    ),

    startDate: (
      <div className="tooltip-options-whitebg">
        <h4>Start Date Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Required field</li>
          <li>Cannot be in the future</li>
          <li>Must be a valid date</li>
          <li className="note">Should match your certificates</li>
        </ul>
      </div>
    ),

    endDate: (
      <div className="tooltip-options-whitebg">
        <h4>End Date Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Required field</li>
          {/* <li>Cannot be in the future</li> */}
          <li>Must be after start date</li>
          <li>Duration max 10 years</li>
          <li className="note">For ongoing education, use expected date</li>
        </ul>
      </div>
    ),

    grade: (
      <div className="tooltip-options-whitebg">
        <h4>Grade/Percentage Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Required field</li>
          <li>Maximum 30 characters</li>
          <li>Accepts percentages, CGPA, letter grades</li>
          <li className="example">Examples: 85%, 8.5 CGPA, A+, First Class</li>
          <li className="note">Enter as per your marksheet</li>
        </ul>
      </div>
    ),

    educationHierarchy: (
      <div className="tooltip-options-whitebg">
        <h4>Education Hierarchy Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>10th required for all higher education</li>
          <li>12th/Diploma required for Bachelor's degrees</li>
          <li>Bachelor's required for Master's degrees</li>
          <li>Master's required for PhD</li>
          <li className="note">Add all your qualifications in order</li>
        </ul>
      </div>
    )
  };

  const handleChange = (index, event) => {
    const { name, value } = event.target || event; // Handle event directly or from target
    const newInputFields = [...inputFields];

    if (name === "department") {
      newInputFields[index][name] = value;
      if (value !== "Other") {
        newInputFields[index].otherDepartment = ""; // Clear otherDepartment if department is not "Other"
      }

      newInputFields[index].stream = ""; // reset stream when degree changes

      const selectedDegrees = newInputFields
        .map(f => f.department)
        .filter(d => d && d !== "Other");
      getFilterDetails(selectedDegrees);

    } else if (name === "otherDepartment") {
      newInputFields[index][name] = value;
    } else if (name === "stream") {
      newInputFields[index][name] = value;
    } else {
      newInputFields[index][name] = value;
    }
    // Update input state for styling
    setInputState({
      ...inputState,
      [`${name}_${index}`]: value ? "green" : "",
    });

    setInputFields(newInputFields);

    // Clear error for this field
    if (value) {
      setErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors[`${name}_${index}`];
        // Clear related errors
        if (name === "department") {
          delete updatedErrors[`10th_missing_${index}`];
          delete updatedErrors[`12th_missing_${index}`];
        }
        return updatedErrors;
      });
    }
  };

  const lastFieldRef = useRef(null);
  const handleAddInputField = () => {
    setInputFields([
      ...inputFields,
      {
        college: "",
        institution_type: "",
        department: "",
        stream: "",
        startDate: "",
        endDate: "",
        grade: "",
        otherDepartment: "",
        user: email,
      },
    ]);

    setTimeout(() => {
      lastFieldRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 100);
  };

  const handleRemoveInputField = (index) => {
    const updatedFields = [...inputFields];
    updatedFields.splice(index, 1);
    setInputFields(updatedFields);

    // Clear errors for removed field
    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      Object.keys(updatedErrors).forEach(key => {
        if (key.endsWith(`_${index}`)) {
          delete updatedErrors[key];
        }
      });
      return updatedErrors;
    });
  };

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false); //loading logic

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setLoading(true); //loading logic
      const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic

      try {
        let res = await axios.post(
          `${BASE_URL}/userdata/EmployeeEducationDetailsall/`,
          inputFields
        );
        if (res.status === 200) {
          forwardroutepath === "workexperiencee" ?
            navigate(`/${forwardroutepath}/${email}/?company_id=${sessionStorage.getItem("company_id") || "null"}`) : navigate(`/${forwardroutepath}/${id}/${email}/`);
        } else {
          alert(res);
        }
      } catch (err) {
        //toast Logic
        handleAllError(err, loadingToastId)
      } finally {
        setLoading(false); //loading logic
        toast.dismiss(loadingToastId);
      }
    }
  };

  const handleFormPut = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setLoading(true); //loading logic
      const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic

      try {
        // Assuming your backend supports updating existing education details via PUT
        await Promise.all(
          inputFields.map(async (educationDetail) => {
            if (educationDetail.id) {
              // Existing education detail, update it
              await axios.put(
                `${BASE_URL}/userdata/EmployeeEducationDetails/${educationDetail.id}/`,
                educationDetail
              );
            } else {
              // New education detail, create it
              await axios.post(
                `${BASE_URL}/userdata/EmployeeEducationDetails/`,
                educationDetail
              );
            }
          })
        );

        forwardroutepath === "workexperiencee" ?
          navigate(`/${forwardroutepath}/${email}/?company_id=${sessionStorage.getItem("company_id") || "null"}`) : navigate(`/${forwardroutepath}/${id}/${email}/`);
      } catch (err) {
        handleAllError(err, loadingToastId)
      } finally {
        setLoading(false); //loading logic
        toast.dismiss(loadingToastId);
      }
    }
  };

  const backroute = () => {
    backroutepath === "personalDetailss" ? navigate(`/${backroutepath}/${email}/?company_id=${sessionStorage.getItem("company_id") || "null"}`) : navigate(`/${backroutepath}/${id}/${email}/`);
  };


  const [data, setData] = useState(false);
  const [buffer, setBuffering] = useState(true); //buffering logic
  const [buffer1, setBuffering1] = useState(true); //buffering logic
  const [streamlist, setStreamList] = useState([]);

  const getUSerDetails = async (email) => {
    setBuffering(true); //buffering logic // Start Buffering
    try {
      const res = await axios.get(
        `${BASE_URL}/userdata/EmployeeEducationDetailsallget/${email}/`
      );
      if (res.data.length > 0) {
        // setInputFields(res.data);
        setInputFields(res.data.sort((a, b) => new Date(a.startDate) - new Date(b.startDate)));
        setData(true);
      }
      else {
        // If no data, reset to a single empty form
        setInputFields([
          {
            college: "",
            institution_type: "",
            department: "10th",
            stream: "",
            startDate: "",
            endDate: "",
            grade: "",
            otherDepartment: "",
            user: email,
          },
        ]);
        setData(false);
      }
    } catch (err) {
      handleAllError(err)
    } finally {
      setBuffering(false);// End Buffering
    }
  };

  const getFilterDetails = async (degrees = []) => {
    setBuffering1(true); //buffering logic // Start Buffering
    try {
      // const streams = await axios.get(
      //   `${BASE_URL}/userdata/get-unique-educationstreams/active/`
      // );
      const res = await axios.post(
        `${BASE_URL}/userdata/get-unique-educationstreams-bydegree/active/`,
        { degrees }
      );

      // const streams = res.data.degrees_streams;

      // const streamOptions = streams.flatMap(item =>
      //   item.streams.map(stream => ({
      //     value: stream,
      //     label: stream,
      //     degree: item.degree
      //   }))
      // );

      // setStreamList(streamOptions);

      const streams = res.data.degrees_streams;

      const degreeStreamMap = {};
      let allStreams = [];

      streams.forEach(item => {
        degreeStreamMap[item.degree] = item.streams;

        item.streams.forEach(stream => {
          if (!allStreams.includes(stream)) {
            allStreams.push(stream);
          }
        });
      });

      setStreamList({
        degreeStreamMap,
        allStreams
      });

    } catch (err) {
      toast.warning('Could not fetch Stream List.');
      // handleAllError(err)
    } finally {
      setBuffering1(false);// End Buffering
    }
  };

  useEffect(() => {
    getUSerDetails(email);
    getFilterDetails([]); // empty → return all
    setShowHierarchyModal(true); // open modal on page load
  }, [email]);

  const getStreamsForDegree = (degree) => {
    if (!streamlist || !streamlist.degreeStreamMap) return [];

    if (!degree) {
      return streamlist.allStreams.map(s => ({
        value: s,
        label: s
      }));
    }

    const streams = streamlist.degreeStreamMap[degree] || streamlist.allStreams;

    return streams.map(s => ({
      value: s,
      label: s
    }));
  };
  const availableDegrees = [
    "10th",
    "12th",
    "B.A. (Bachelor of Arts)",
    "B.Com. (Bachelor of Commerce)",
    "M.Com. (Masters of Commerce)",
    "MBA (Master of Business Administration)",
    "M.Tech. (Master of Technology)",
    "M.E. (Master of Engineering )",
    "B.Tech. (Bachelor of Technology)",
    "B.E. (Bachelor of Engineering )",
    "B.Arch. (Bachelor of Architecture)",
    "Diploma",
    "PhD",
    "ITI",
    "Other",
  ];
  return (
    <>
      {buffer ? <Loader /> : null}{/* //buffering logic */}
      <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

      <div className="jd-heading-outer">
        {/* Education Details */}
        <div className="jd-heading-outer-flex">
          <h2 className="jd-heading-main font-weight600  font-size-heading">
            <CustomTooltip position="top" tooltipContent={tooltipContent.educationHierarchy}>
              Education Details*
              <InfoIcon />
            </CustomTooltip>
          </h2>
          {inputFields.length > 1 && <button
            type="button"
            onClick={handleAddInputField}
            className="jd-heading-main font-weight600 font-size-text flex-row"
          >
            Add more{" "}
            <AddwithBlueCircle />
          </button>}
        </div>
        <div>
          <div className="jd-heading-bottom-bold"></div>
          <div className="jd-heading-bottom-light"></div>
        </div>
      </div>

      {showHierarchyModal && (
        <Modal show={showHierarchyModal} onHide={() => setShowHierarchyModal(false)} dialogClassName="request-leave">
          <Modal.Header closeButton>
            <Modal.Title>Education Hierarchy Rules</Modal.Title>
          </Modal.Header>
          <Modal.Body closeButton>
            <div className="tooltip-options-whitebg">
              <ul className="tooltip-list">
                <li>10th required for all higher education</li>
                <li>12th/Diploma required for Bachelor's degrees</li>
                <li>Bachelor's required for Master's degrees</li>
                <li>Master's required for PhD</li>
                <li className="note">Add all your qualifications in order</li>
              </ul>
            </div>
          </Modal.Body>
        </Modal>
      )}

      <form className="register-form">
        {inputFields.map((field, index) => (
          <>
            <div className="jd-heading-outer-flex">
              <h2 className="jd-heading-main font-weight600  font-size-heading">{index + 1}{getNumberSuffix(index + 1)} Education Details</h2>
              <h2 className="jd-heading-main-right font-size-text ">
                {
                  field.id
                    ?
                    <DeleteEducation i={field} email={email} getUserDetails={getUSerDetails} />
                    :
                    index > 0 ? (
                      <button
                        type="button"
                        onClick={() => handleRemoveInputField(index)}
                        className="minor-button  model-button-inactive font-weight500 font-size-heading"
                      >
                        Remove
                      </button>
                    ) : (
                      ""
                    )
                }
              </h2>
            </div>
            <div key={index} className="form-flex-wrap">

              {/* College/Institution */}
              <div
                ref={index === inputFields.length - 1 ? lastFieldRef : null}
                className="flex-column width-flex-50"
              >
                <label htmlFor={`college_${index}`} className="form-labels font-weight500    font-size-subheading">
                  <CustomTooltip position="top" tooltipContent={tooltipContent.college}>
                    College/Institution<span className="required">*</span>
                    {/* <InfoIcon /> */}
                  </CustomTooltip>
                </label>
                <input
                  type="text"
                  id={`college_${index}`}
                  maxLength={100}
                  name="college"
                  value={field.college}
                  onChange={(e) => {
                    handleChange(index, e);
                    // handleFileChange(e)
                  }}
                  placeholder="Enter Institute Name"
                  className={`text-capitalize form-input width-respo ${errors[`college_${index}`]
                    ? "error"
                    : field.college
                      ? "success"
                      : ""
                    }`}
                />
                {errors[`college_${index}`] && (
                  <span className="error-message font-size-text ">
                    {errors[`college_${index}`]}
                  </span>
                )}
              </div>

              {/* Degree Type */}
              <div className="flex-column width-flex-50">
                <label htmlFor="department" className="form-labels font-weight500    font-size-subheading">
                  <CustomTooltip position="top" tooltipContent={tooltipContent.degreeType}>
                    Degree Type<span className="required">*</span>
                    <InfoIcon />
                  </CustomTooltip>
                </label>
                <select
                  id="department"
                  name="department"
                  value={field.department}
                  onChange={(e) => handleChange(index, e)}
                  className={`form-input width-respo ${errors[`department_${index}`]
                    ? "error"
                    : field.department
                      ? "success"
                      : ""
                    }`}
                >
                  <option value="">Select a Degree Type</option>
                  {/* If field.department is not in availableDegrees, add it as an extra option */}
                  {!availableDegrees.includes(field.department) && field.department && (
                    <option value={field.department}>
                      {field.department}
                    </option>
                  )}
                  {availableDegrees.map((degree) =>
                    inputFields.some((f) => f.department === degree && f !== field) ? null : (
                      <option key={degree} value={degree}>
                        {degree}
                      </option>
                    )
                  )}
                </select>
                {errors[`department_${index}`] && (
                  <span className="error-message font-size-text ">
                    {errors[`department_${index}`]}
                  </span>
                )}
              </div>

              {/* Other Degree */}
              {field.department === "Other" && (
                <div className="flex-column width-flex-50">
                  <label htmlFor={`otherDepartment_${index}`} className="form-labels font-weight500    font-size-subheading">
                    <CustomTooltip position="top" tooltipContent={tooltipContent.otherDegree}>
                      Specify Other Degree<span className="required">*</span>
                      <InfoIcon />
                    </CustomTooltip>
                  </label>
                  <input
                    type="text"
                    id={`otherDepartment_${index}`}
                    maxLength={100}
                    name="otherDepartment" // Correctly name the input
                    value={field.otherDepartment} // Bind to otherDepartment
                    onChange={(e) => handleChange(index, e)}
                    className={`form-input width-respo ${errors[`department_${index}`]
                      ? "error"
                      : field.otherDepartment
                        ? "success"
                        : ""
                      }`}
                  />
                  {errors[`department_${index}`] && (
                    <span className="error-message font-size-text ">
                      {errors[`department_${index}`]}
                    </span>
                  )}
                </div>
              )}

              {/* Stream */}
              <div className="flex-column width-flex-50">
                <label htmlFor={`stream_${index}`} className="form-labels font-weight500    font-size-subheading">
                  <CustomTooltip position="top" tooltipContent={tooltipContent.stream}>
                    Stream<span className="required">*</span>
                    <InfoIcon />
                  </CustomTooltip>
                </label>
                {buffer1 ?
                  <div className="form-loader-date">
                    <div className="bar-loader"></div>
                  </div>
                  : <CreatableSelect
                    id={`stream_${index}`}
                    // options={streamlist}
                    options={getStreamsForDegree(field.department)}
                    value={field.stream ? { value: field.stream, label: field.stream } : null}  // Ensure state reflects this
                    onChange={(option) => handleChange(index, { target: { name: 'stream', value: option ? option.value : '' } })}
                    placeholder="Select or Type a new stream"
                    className={`width-respo ${errors[`stream_${index}`] ? 'error' : field.stream ? 'success' : ''}`}
                    // isClearable
                    formatCreateLabel={(inputValue) => `Create new stream: ${inputValue}`}
                    isValidNewOption={(inputValue, selectValue, selectOptions) =>
                      inputValue.trim() !== '' &&
                      !selectOptions.find(option => option.label.toLowerCase() === inputValue.toLowerCase())
                    }
                    // styles={CSSForSelect}
                    styles={CSSForSelectAutoSize}
                  />}
                {errors[`stream_${index}`] && (
                  <>
                    <br />
                    <span className="error-message font-size-text ">
                      {errors[`stream_${index}`]}
                    </span>
                  </>
                )}
              </div>

              {/* Institution Type */}
              <div className="flex-column width-flex-50">
                <label htmlFor="institution_type" className="form-labels font-weight500    font-size-subheading">
                  <CustomTooltip position="top" tooltipContent={tooltipContent.institutionType}>
                    Institution Type<span className="required">*</span>
                    {/* <InfoIcon /> */}
                  </CustomTooltip>
                </label>
                <select
                  id={`institution_type_${index}`}
                  name="institution_type"
                  value={field.institution_type}
                  onChange={(e) => handleChange(index, e)}
                  className={`form-input width-respo ${errors[`institution_type_${index}`]
                    ? "error"
                    : field.institution_type
                      ? "success"
                      : ""
                    }`}
                >
                  <option value="">Select a Institution Type</option>
                  <option value="Government">Government</option>
                  <option value="Private">Private</option>
                </select>
                {errors[`institution_type_${index}`] && (
                  <span className="error-message font-size-text ">
                    {errors[`institution_type_${index}`]}
                  </span>
                )}
              </div>

              {/* Start Date */}
              <div className="flex-column width-flex-50">
                <label htmlFor={`startDate_${index}`} className="form-labels font-weight500    font-size-subheading">
                  <CustomTooltip position="top" tooltipContent={tooltipContent.startDate}>
                    Start Date<span className="required">*</span>
                    {/* <InfoIcon /> */}
                  </CustomTooltip>
                </label>
                <input
                  type="date"
                  id={`startDate_${index}`}
                  name="startDate"
                  value={field.startDate}
                  onChange={(e) => {
                    const value = e.target.value;
                    const date = new Date(value);
                    const today = new Date();

                    if (value && (isNaN(date.getTime()) || date > today)) {
                      return;
                    }

                    handleChange(index, e);
                  }}
                  max={new Date().toISOString().split("T")[0]}
                  className={`form-input width-respo ${errors[`startDate_${index}`]
                    ? "error"
                    : field.startDate
                      ? "success"
                      : ""
                    }`}
                />
                {errors[`startDate_${index}`] && (
                  <span className="error-message font-size-text ">
                    {errors[`startDate_${index}`]}
                  </span>
                )}
              </div>

              {/* End Date */}
              <div className="flex-column width-flex-50">
                <label htmlFor={`endDate_${index}`} className="form-labels font-weight500    font-size-subheading">
                  <CustomTooltip position="top" tooltipContent={tooltipContent.endDate}>
                    End Date<span className="required">*</span>
                    {/* <InfoIcon /> */}
                  </CustomTooltip>
                </label>
                <input
                  type="date"
                  id={`endDate_${index}`}
                  name="endDate"
                  value={field.endDate}
                  onChange={(e) => handleChange(index, e)}
                  // onChange={(e) => {
                  //   const value = e.target.value;

                  //   // Allow typing until full date is entered
                  //   if (!value || value.length < 10) {
                  //     handleChange(index, e);
                  //     return;
                  //   }

                  //   const date = new Date(value);
                  //   if (isNaN(date.getTime())) return;

                  //   const year = date.getFullYear();
                  //   if (year < 1000 || year > 9999) return;

                  //   let maxAllowed;

                  //   if (field.startDate) {
                  //     const start = new Date(field.startDate);

                  //     if (date < start) return;

                  //     maxAllowed = new Date(start);
                  //     maxAllowed.setFullYear(start.getFullYear() + 10);
                  //   } else {
                  //     maxAllowed = new Date();
                  //     maxAllowed.setFullYear(maxAllowed.getFullYear() + 10);
                  //   }

                  //   if (date > maxAllowed) return;

                  //   handleChange(index, e);
                  // }}
                  onBlur={(e) => {
                    const value = e.target.value;
                    if (!value) return;

                    const date = new Date(value);
                    if (isNaN(date.getTime())) return;

                    const year = date.getFullYear();
                    if (year < 1000 || year > 9999) return;

                    let maxAllowed;

                    if (field.startDate) {
                      const start = new Date(field.startDate);

                      if (date < start) {
                        toast.warning("End date must be after start date");
                        return;
                      }

                      maxAllowed = new Date(start);
                      maxAllowed.setFullYear(start.getFullYear() + 10);
                    } else {
                      maxAllowed = new Date();
                      maxAllowed.setFullYear(maxAllowed.getFullYear() + 10);
                    }

                    if (date > maxAllowed) {
                      toast.warning("Duration cannot exceed 10 years");
                    }
                  }}
                  min={field.startDate || ""}
                  max={
                    field.startDate
                      ? new Date(
                        new Date(field.startDate).setFullYear(
                          new Date(field.startDate).getFullYear() + 10
                        )
                      ).toISOString().split("T")[0]
                      : new Date(
                        new Date().setFullYear(new Date().getFullYear() + 10)
                      ).toISOString().split("T")[0]
                  }
                  className={`form-input width-respo ${errors[`endDate_${index}`]
                    ? "error"
                    : field.endDate
                      ? "success"
                      : ""
                    }`}
                />
                {errors[`endDate_${index}`] && (
                  <span className="error-message font-size-text ">
                    {errors[`endDate_${index}`]}
                  </span>
                )}
              </div>

              <div className="flex-column width-flex-50">
                <label htmlFor={`grade_${index}`} className="form-labels font-weight500    font-size-subheading">
                  <CustomTooltip position="top" tooltipContent={tooltipContent.grade}>
                    Grade/Percentage<span className="required">*</span>
                    {/* <InfoIcon /> */}
                  </CustomTooltip>
                </label>
                <input
                  type="text"
                  id={`grade_${index}`}
                  maxLength={30}
                  name="grade"
                  value={field.grade}
                  onChange={(e) => handleChange(index, e)}
                  placeholder="e.g., 85%, 8.5 CGPA, A+"
                  className={`form-input width-respo ${errors[`grade_${index}`]
                    ? "error"
                    : field.grade
                      ? "success"
                      : ""
                    }`}
                />
                {errors[`grade_${index}`] && (
                  <span className="error-message font-size-text ">
                    {errors[`grade_${index}`]}
                  </span>
                )}
              </div>
            </div>

            {/* <div id={`department-error-${index}`}>
              {errors[`10th_missing_${index}`] && (
                <span className="error-message font-size-text ">
                  {errors[`10th_missing_${index}`]}
                </span>
              )}{" "}
              <br />
              {errors[`12th_missing_${index}`] && (
                <span className="error-message font-size-text ">
                  {errors[`12th_missing_${index}`]}
                </span>
              )}
            </div> */}

            {/* Hierarchy Errors */}
            <div className="error-message font-size-text">
              {errors[`10th_missing_${index}`] && (
                <span>{errors[`10th_missing_${index}`]}</span>
              )}
              {errors[`12th_missing_${index}`] && (
                <span>{errors[`12th_missing_${index}`]}</span>
              )}
              {errors[`bachelor_missing_${index}`] && (
                <span>{errors[`bachelor_missing_${index}`]}</span>
              )}
            </div>

            <div className="jd-heading-outer">
              <div className="jd-heading-bottom-light"></div>
            </div>
          </>
        ))}

        {/* Add More Button */}
        <div className="jd-heading-outer">
          <div className="jd-heading-outer-flex">
            <button
              type="button"
              onClick={handleAddInputField}
              className="jd-heading-main font-weight600 font-size-text"
            >
              <AddwithBlueCircle />
              {" "} Please click here to add more
            </button>
          </div>
        </div>
        <div className="button-models">
          <button className="model-button model-button-cancel font-weight500" onClick={backroute} disabled={loading}>
            Back
          </button>
          {data ? (
            <button
              onClick={handleFormPut}
              className="model-button  font-weight500    model-button-submit"
              disabled={loading}
            >
              {loading ? "Updating..." : "Submit & Proceed"}
            </button>
          ) : (
            <button
              onClick={handleFormSubmit}
              className="model-button  font-weight500    model-button-submit"
              disabled={loading}
            >
              {loading ? "Saving..." : "Next"}
            </button>
          )}
        </div>
        <p className="error-message font-size-text">
          {isSubmitted && Object.keys(errors).length > 0 && (
            Object.keys(errors).length > 5 ? (
              <h5 className="text-center">Please fill all mandatory fields!</h5>
            ) : (
              <h6 className="text-center">
                {Object.keys(errors).map((field) =>
                  field.charAt(0).toUpperCase() + field.slice(1)
                ).join(', ') + ' are required!'}
              </h6>
            )
          )}
        </p>
      </form>
    </>
  );
};

const DeleteEducation = ({ i, email, getUserDetails }) => {
  const [show, setShow] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = (e) => {
    e.preventDefault();
    setShow(true);
  };


  const [loading, setLoading] = useState(false); //loading logic
  const handleFormSubmitDelete = async (e) => {
    e.preventDefault();
    setLoading(true); //loading logic
    const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic
    try {
      let res = await axios.delete(`${BASE_URL}/userdata/EmployeeEducationDetails/${i.id}/`);

      if (res.status === 200) {
        await getUserDetails(email);
        setShow(false);
      } else {
        alert(res);
      }
    } catch (err) {
      handleErrorToast(err, loadingToastId)
    } finally {
      setLoading(false); //loading logic
      toast.dismiss(loadingToastId);
    }
  };

  return (
    <>
      <button
        className="minor-button model-button-delete"
        title="Delete Education Details"
        onClick={(e) => handleShow(e)}
      >
        <DeleteDustbin />
      </button>

      <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
        <Modal.Header closeButton>
          <Modal.Title className="align-center">Delete Education Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="parent-div align-center">
            Are You Sure You Want to delete Education Details - {i.college}
            <div className="button-models">
              <button className="model-button model-button-cancel font-weight500 " onClick={handleClose}>
                Cancel
              </button>
              <button
                onClick={handleFormSubmitDelete}
                className="model-button model-button-delete font-weight500 "
                disabled={loading}
              >
                Delete
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

const WorkexperienceAdd = ({ email, id, forwardroutepath, backroutepath }) => {
  const [inputFields, setInputFields] = useState([
    {
      company: "",
      startDate: "",
      endDate: "",
      jobposition: "",
      jobtype: "",
      monthlyCtc: "",
      monthlyInhand: "",
      user: email,
    },
  ]);
  const [errors, setErrors] = useState({});
  const [inputState, setInputState] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showExperienceModal, setShowExperienceModal] = useState(false);

  const lastFieldRef = useRef(null);

  // Validation functions
  const validateCompany = (company) => {
    if (!company) return false;
    return company.length >= 2 && company.length <= 100;
  };

  const validateJobPosition = (position) => {
    if (!position) return false;
    return position.length >= 2 && position.length <= 100;
  };

  const validateDates = (startDate, endDate) => {
    if (!startDate || !endDate) return { valid: false };

    const start = new Date(startDate);
    const end = new Date(endDate);
    const currentDate = new Date();

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return { valid: false, error: "Invalid date format" };
    }

    if (start > currentDate) {
      return { valid: false, error: "Start date cannot be in the future" };
    }

    if (start > end) {
      return { valid: false, error: "Start date must be before end date" };
    }

    // Check if duration is reasonable (max 50 years career span)
    const yearDiff = end.getFullYear() - start.getFullYear();
    if (yearDiff > 50) {
      return { valid: false, error: "Experience duration too long (max 50 years)" };
    }

    return { valid: true };
  };

  const validateSalary = (salary) => {
    if (!salary) return false;
    // Allow numbers with optional commas and decimal points
    const salaryRegex = /^\d{1,3}(,\d{3})*(\.\d{1,2})?$/;
    return salary.length <= 15;
  };

  const validateForm = () => {
    const newErrors = {};
    // inputFields.length >= 2 &&
    inputFields.forEach((field, index) => {
      // Only validate if company is filled or there are multiple entries
      const shouldValidate = field.company !== "" || inputFields.length > 1;

      if (shouldValidate) {
        const requiredFields = [
          "company",
          "jobposition",
          "startDate",
          "endDate",
          "jobtype",
          "monthlyCtc",
          "monthlyInhand",
        ];
        requiredFields.forEach((reqField) => {
          if (!field[reqField]) {
            newErrors[`${reqField}_${index}`] = `${reqField.charAt(0).toUpperCase() + reqField.slice(1)
              } is required!`;
          }
        });
        // Company validation
        if (field.company && !validateCompany(field.company)) {
          newErrors[`company_${index}`] = "Company name must be between 2 and 100 characters!";
        }

        // Job position validation
        if (field.jobposition && !validateJobPosition(field.jobposition)) {
          newErrors[`jobposition_${index}`] = "Job position must be between 2 and 100 characters!";
        }

        // Salary validations
        if (field.monthlyCtc && !validateSalary(field.monthlyCtc)) {
          newErrors[`monthlyCtc_${index}`] = "Please enter a valid CTC amount!";
        }

        if (field.monthlyInhand && !validateSalary(field.monthlyInhand)) {
          newErrors[`monthlyInhand_${index}`] = "Please enter a valid in-hand amount!";
        }

        // Date validation
        if (field.startDate && field.endDate) {
          const dateValidation = validateDates(field.startDate, field.endDate);
          if (!dateValidation.valid) {
            if (dateValidation.error.includes("Start date")) {
              newErrors[`startDate_${index}`] = dateValidation.error;
            } else {
              newErrors[`endDate_${index}`] = dateValidation.error;
            }
          }
        }
      }
    });

    // // Check for overlapping experiences (optional)
    // const experienceOverlapError = checkForOverlappingExperiences();
    // if (experienceOverlapError) {
    //   newErrors.overlap_error = experienceOverlapError;
    //   setShowExperienceModal(true);
    // }

    setErrors(newErrors);
    setIsSubmitted(true);

    return Object.keys(newErrors).length === 0;
  };

  const checkForOverlappingExperiences = () => {
    const validExperiences = inputFields.filter(f => f.company && f.startDate && f.endDate);

    for (let i = 0; i < validExperiences.length; i++) {
      for (let j = i + 1; j < validExperiences.length; j++) {
        const exp1 = validExperiences[i];
        const exp2 = validExperiences[j];

        const start1 = new Date(exp1.startDate);
        const end1 = new Date(exp1.endDate);
        const start2 = new Date(exp2.startDate);
        const end2 = new Date(exp2.endDate);

        if (start1 <= end2 && start2 <= end1) {
          return "Work experiences cannot overlap. Please check the dates.";
        }
      }
    }
    return null;
  };


  // Tooltip content
  const tooltipContent = {
    company: (
      <div className="tooltip-options-whitebg">
        <h4>Company Name Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Required if you're adding work experience</li>
          <li>Minimum 2 characters</li>
          <li>Maximum 100 characters</li>
          <li className="example">Example: Google, Microsoft, Tata Consultancy Services</li>
          <li className="note">Enter full company name</li>
          <li className="note">Leave all fields empty to skip this section</li>
        </ul>
      </div>
    ),

    jobPosition: (
      <div className="tooltip-options-whitebg">
        <h4>Job Position Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Required if you're adding work experience</li>
          <li>Minimum 2 characters</li>
          <li>Maximum 100 characters</li>
          <li className="example">Examples: Software Engineer, Team Lead, Manager</li>
          <li className="note">Enter your official designation</li>
        </ul>
      </div>
    ),

    startDate: (
      <div className="tooltip-options-whitebg">
        <h4>Start Date Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Required if you're adding work experience</li>
          <li>Cannot be in the future</li>
          <li>Must be a valid date</li>
          <li className="note">As per your joining date</li>
        </ul>
      </div>
    ),

    endDate: (
      <div className="tooltip-options-whitebg">
        <h4>End Date Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Required if you're adding work experience</li>
          <li>Must be after start date</li>
          <li>Maximum duration: 50 years</li>
          <li>For current job, you can enter expected date</li>
          <li className="note">Leave empty if currently working here</li>
        </ul>
      </div>
    ),

    jobType: (
      <div className="tooltip-options-whitebg">
        <h4>Job Type Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Required if you're adding work experience</li>
          <li>Select from available options</li>
          <li className="example">Full Time: Permanent employment</li>
          <li className="example">Contract: Fixed term contract</li>
          <li className="example">Internship: Training period</li>
        </ul>
      </div>
    ),

    salary: (
      <div className="tooltip-options-whitebg">
        <h4>Salary Rules</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Required if you're adding work experience</li>
          {/* <li>Maximum 15 characters</li> */}
          <li>Accepts numbers with commas</li>
          <li className="example">Examples: 50,000 or 75,000.50</li>
          <li className="note">Enter monthly amounts in ₹</li>
        </ul>
      </div>
    ),

    experienceGuidelines: (
      <div className="tooltip-options-whitebg">
        <h4>Experience Guidelines</h4>
        <hr className="line" />
        <ul className="tooltip-list">
          <li>Add all work experiences in chronological order</li>
          <li>Experiences should not overlap</li>
          <li>Maximum 50 years total experience per entry</li>
          <li>Include internships and contracts</li>
          <li className="note">Leave ALL fields empty to skip this section</li>
        </ul>
      </div>
    )
  };
  const handleChange = (index, event) => {
    const { name, value } = event.target;
    const newInputFields = [...inputFields];
    newInputFields[index][name] = value;

    // Update input state for styling
    setInputState({
      ...inputState,
      [`${name}_${index}`]: value ? "green" : "",
    });

    setInputFields(newInputFields);

    // Clear error for this field
    if (value) {
      setErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors[`${name}_${index}`];
        return updatedErrors;
      });
    }
  };

  const handleAddInputField = () => {
    setInputFields([
      ...inputFields,
      {
        company: "",
        startDate: "",
        endDate: "",
        jobposition: "",
        jobtype: "",
        monthlyCtc: "",
        monthlyInhand: "",
        user: email,
      },
    ]);

    setTimeout(() => {
      lastFieldRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 100);
  };

  const handleClearInputField = (index) => {
    const updatedFields = [...inputFields];

    updatedFields[index] = {
      ...updatedFields[index],
      company: "",
      startDate: "",
      endDate: "",
      jobposition: "",
      jobtype: "",
      monthlyCtc: "",
      monthlyInhand: "",
    };

    setInputFields(updatedFields);

    // Clear errors for that row
    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      Object.keys(updatedErrors).forEach((key) => {
        if (key.endsWith(`_${index}`)) {
          delete updatedErrors[key];
        }
      });
      return updatedErrors;
    });
  };

  const handleRemoveInputField = (index) => {
    const updatedFields = [...inputFields];
    updatedFields.splice(index, 1);
    setInputFields(updatedFields);

    // Clear errors for removed field
    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      Object.keys(updatedErrors).forEach(key => {
        if (key.endsWith(`_${index}`)) {
          delete updatedErrors[key];
        }
      });
      return updatedErrors;
    });
  };

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false); //loading logic

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setLoading(true); //loading logic
      const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic

      try {
        let res = await axios.post(
          `${BASE_URL}/userdata/userworkexpbulkdata/`,
          inputFields
        );
        if (res.status === 200) {
          forwardroutepath === "emergencycontacts" ?
            navigate(`/${forwardroutepath}/${email}/?company_id=${sessionStorage.getItem("company_id") || "null"}`) : navigate(`/${forwardroutepath}/${id}/${email}/`);
        } else {
          alert(res);
        }
      } catch (err) {
        handleErrorToast(err, loadingToastId)
      } finally {
        setLoading(false); //loading logic
        toast.dismiss(loadingToastId);
      }
    }
  };

  const handleFormPut = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setLoading(true); //loading logic
      const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic
      try {
        // Assuming your backend supports updating existing education details via PUT
        await Promise.all(
          inputFields.map(async (workexperience) => {
            if (workexperience.id) {
              // Existing education detail, update it
              await axios.put(
                `${BASE_URL}/userdata/userworkexp/${workexperience.id}/`,
                workexperience
              );
            } else if (workexperience.company != "") {
              // New education detail, create it
              await axios.post(
                `${BASE_URL}/userdata/userworkexp/`,
                workexperience
              );
            }
          })
        );
        forwardroutepath === "emergencycontacts" ?
          navigate(`/${forwardroutepath}/${email}/?company_id=${sessionStorage.getItem("company_id") || "null"}`) : navigate(`/${forwardroutepath}/${id}/${email}/`);
      } catch (err) {
        //toast Logic
        handleErrorToast(err, loadingToastId)
      } finally {
        setLoading(false); //loading logic
        toast.dismiss(loadingToastId);
      }
    }
  };

  const backroute = () => {
    backroutepath === "educationDetailss" ? navigate(`/${backroutepath}/${email}/?company_id=${sessionStorage.getItem("company_id") || "null"}`) : navigate(`/${backroutepath}/${id}/${email}/`);
  };


  const [data, setData] = useState(false);
  const [buffer, setBuffering] = useState(true); //buffering logic

  const getUSerDetails = async (email) => {
    setBuffering(true); //buffering logic // Start Buffering
    try {
      const res = await axios.get(
        `${BASE_URL}/userdata/userworkexpallget/${email}/`
      );
      if (res.data.length !== 0) {
        setInputFields(res.data.sort((a, b) => new Date(a.startDate) - new Date(b.startDate)));
      }
      else {
        // If no data, reset to a single empty form
        setInputFields([
          {
            company: "",
            startDate: "",
            endDate: "",
            jobposition: "",
            jobtype: "",
            monthlyCtc: "",
            monthlyInhand: "",
            user: email,
          },
        ]);
        setData(false);
      }
      setData(true);
    } catch (err) {
      handleAllError(err)
    } finally {
      setBuffering(false);// End Buffering
    }
  };

  useEffect(() => {
    getUSerDetails(email);
  }, [email]);

  return (
    <>
      {buffer ? <Loader /> : null}{/* //buffering logic */}
      <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
      <div className="jd-heading-outer">
        <div className="jd-heading-outer-flex">
          <h2 className="jd-heading-main font-weight600  font-size-heading">
            <CustomTooltip position="top" tooltipContent={tooltipContent.experienceGuidelines}>
              Work Experience *
              <InfoIcon />
            </CustomTooltip>
          </h2>
          {inputFields.length > 1 && <button
            type="button"
            onClick={handleAddInputField}
            className="jd-heading-main font-weight600 font-size-text "
          >
            Add more{" "}
            <AddwithBlueCircle />
          </button>}
        </div>
        <div>
          <div className="jd-heading-bottom-bold"></div>
          <div className="jd-heading-bottom-light"></div>
        </div>
      </div >

      {showExperienceModal && (
        <Modal show={showExperienceModal} onHide={() => setShowExperienceModal(false)} dialogClassName="request-leave">
          <Modal.Header closeButton>
            <Modal.Title>Experience Guidelines</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="tooltip-options-whitebg">
              <ul className="tooltip-list">
                <li>Work experiences should not overlap</li>
                <li>Add experiences in chronological order</li>
                <li>Maximum 50 years total experience</li>
                <li>For current job, leave end date empty</li>
                <li className="note">Please review your experience dates</li>
              </ul>
            </div>
          </Modal.Body>
        </Modal>
      )
      }

      <form className="register-form" >
        {inputFields.map((field, index) => (
          <>
            <div className="jd-heading-outer-flex">
              <h2 className="jd-heading-main font-weight600  font-size-heading">{index + 1}{getNumberSuffix(index + 1)} Work Experience</h2>
              <h2 className="jd-heading-main-right font-size-text ">
                {
                  field.id
                    ?
                    <DeletWorkExperience i={field} email={email} getUserDetails={getUSerDetails} />
                    :
                    index > 0 ? (
                      <button
                        type="button"
                        onClick={() => handleRemoveInputField(index)}
                        className="minor-button model-button-inactive font-weight500 font-size-heading"
                      >
                        Remove
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleClearInputField(index)}
                        className="minor-button model-button-inactive font-weight500 font-size-heading"
                      >
                        Clear
                      </button>
                    )
                }
              </h2>
            </div>
            <div
              ref={index === inputFields.length - 1 ? lastFieldRef : null}
              className="form-flex-wrap"
            >

              {/* Company Name */}
              <div className="flex-column width-flex-50">
                <label htmlFor={`company_${index}`} className="form-labels font-weight500    font-size-subheading">
                  <CustomTooltip position="top" tooltipContent={tooltipContent.company}>
                    Company Name{inputFields[index].company != "" || inputFields.length > 1 ? <span className="required">*</span> : ""}
                    <InfoIcon />
                  </CustomTooltip>
                </label>
                <input
                  type="text"
                  id={`company_${index}`}
                  maxLength={100}
                  name="company"
                  value={field.company}
                  onChange={(e) => handleChange(index, e)}
                  placeholder="Enter Company Name"
                  className={`text-capitalize form-input width-respo ${errors[`company_${index}`]
                    ? "error"
                    : field.company
                      ? "success"
                      : ""
                    }`}
                />
                {errors[`company_${index}`] && (
                  <span className="error-message font-size-text ">
                    {errors[`company_${index}`]}
                  </span>
                )}
              </div>

              {/* Job Position */}
              <div className="flex-column width-flex-50">
                <label htmlFor={`jobposition_${index}`} className="form-labels font-weight500    font-size-subheading">
                  <CustomTooltip position="top" tooltipContent={tooltipContent.jobPosition}>
                    Job Position{inputFields[index].company != "" || inputFields.length > 1 ? <span className="required">*</span> : ""}
                  </CustomTooltip>
                </label>
                <input
                  type="text"
                  id={`jobposition_${index}`}
                  maxLength={100}
                  name="jobposition"
                  value={field.jobposition}
                  onChange={(e) => handleChange(index, e)}
                  placeholder="Enter Job Position"
                  className={`text-capitalize form-input width-respo ${errors[`jobposition_${index}`]
                    ? "error"
                    : field.jobposition
                      ? "success"
                      : ""
                    }`}
                />
                {errors[`jobposition_${index}`] && (
                  <span className="error-message font-size-text ">
                    {errors[`jobposition_${index}`]}
                  </span>
                )}
              </div>

              {/* Start Date */}
              <div className="flex-column width-flex-50">
                <label htmlFor={`startDate_${index}`} className="form-labels font-weight500    font-size-subheading">
                  <CustomTooltip position="top" tooltipContent={tooltipContent.startDate}>
                    Start Date{inputFields[index].company != "" || inputFields.length > 1 ? <span className="required">*</span> : ""}
                  </CustomTooltip>
                </label>
                <input
                  type="date"
                  id={`startDate_${index}`}
                  name="startDate"
                  value={field.startDate}
                  // onChange={(e) => handleChange(index, e)}
                  onChange={(e) => {
                    const value = e.target.value;
                    const date = new Date(value);
                    const today = new Date();

                    if (value && (isNaN(date.getTime()) || date > today)) {
                      return;
                    }

                    handleChange(index, e);
                  }}
                  max={new Date().toISOString().split("T")[0]}
                  className={`form-input width-respo ${errors[`startDate_${index}`]
                    ? "error"
                    : field.startDate
                      ? "success"
                      : ""
                    }`}
                />
                {errors[`startDate_${index}`] && (
                  <span className="error-message font-size-text ">
                    {errors[`startDate_${index}`]}
                  </span>
                )}
              </div>

              {/* End Date */}
              <div className="flex-column width-flex-50">
                <label htmlFor={`endDate_${index}`} className="form-labels font-weight500    font-size-subheading">
                  <CustomTooltip position="top" tooltipContent={tooltipContent.endDate}>
                    End Date{inputFields[index].company != "" || inputFields.length > 1 ? <span className="required">*</span> : ""}
                  </CustomTooltip>
                </label>
                <input
                  type="date"
                  id={`endDate_${index}`}
                  name="endDate"
                  value={field.endDate}
                  onChange={(e) => handleChange(index, e)}
                  onBlur={(e) => {
                    const value = e.target.value;
                    if (!value) return;

                    const date = new Date(value);
                    if (isNaN(date.getTime())) return;

                    const year = date.getFullYear();
                    if (year < 1000 || year > 9999) return;

                    let maxAllowed;

                    if (field.startDate) {
                      const start = new Date(field.startDate);

                      if (date < start) {
                        toast.warning("End date must be after start date");
                        return;
                      }

                      maxAllowed = new Date(start);
                      maxAllowed.setFullYear(start.getFullYear() + 50);
                    } else {
                      maxAllowed = new Date();
                      maxAllowed.setFullYear(maxAllowed.getFullYear() + 50);
                    }

                    if (date > maxAllowed) {
                      toast.warning("Duration cannot exceed 10 years");
                    }
                  }}
                  min={field.startDate || ""}
                  max={
                    field.startDate
                      ? new Date(
                        new Date(field.startDate).setFullYear(
                          new Date(field.startDate).getFullYear() + 50
                        )
                      ).toISOString().split("T")[0]
                      : new Date(
                        new Date().setFullYear(new Date().getFullYear() + 50)
                      ).toISOString().split("T")[0]
                  }
                  className={`form-input width-respo ${errors[`endDate_${index}`]
                    ? "error"
                    : field.endDate
                      ? "success"
                      : ""
                    }`}
                />
                {errors[`endDate_${index}`] && (
                  <span className="error-message font-size-text ">
                    {errors[`endDate_${index}`]}
                  </span>
                )}
              </div>

              {/* Job Type */}
              <div className="flex-column width-flex-50">
                <label className="form-labels font-weight500    font-size-subheading" htmlFor={`jobtype_${index}`}>
                  <CustomTooltip position="top" tooltipContent={tooltipContent.jobType}>
                    Job Type{inputFields[index].company != "" || inputFields.length > 1 ? <span className="required">*</span> : ""}
                  </CustomTooltip>
                </label>
                <select
                  id={`jobtype_${index}`}
                  name="jobtype"
                  value={field.jobtype}
                  onChange={(e) => handleChange(index, e)}
                  className={`form-input width-respo ${errors[`jobtype_${index}`]
                    ? "error"
                    : field.jobtype
                      ? "success"
                      : ""
                    }`}
                >
                  <option value="">Select Job Type</option>
                  <option value="full-time">Full Time</option>
                  <option value="part-time">Part Time</option>
                  <option value="contract">Contract</option>
                  <option value="freelance">Freelance</option>
                  <option value="internship">Internship</option>
                </select>
                {errors[`jobtype_${index}`] && (
                  <span className="error-message font-size-text ">
                    {errors[`jobtype_${index}`]}
                  </span>
                )}
              </div>

              {/* Monthly CTC */}
              <div className="flex-column width-flex-50">
                <label htmlFor={`monthlyCtc_${index}`} className="form-labels font-weight500    font-size-subheading">
                  <CustomTooltip position="top" tooltipContent={tooltipContent.salary}>
                    Monthly CTC (₹){inputFields[index].company != "" || inputFields.length > 1 ? <span className="required">*</span> : ""}
                  </CustomTooltip>
                </label>
                <input
                  type="text"
                  id={`monthlyCtc_${index}`}
                  maxLength={15}
                  name="monthlyCtc"
                  value={field.monthlyCtc}
                  // onChange={(e) => handleChange(index, e)}
                  onChange={(e) => {
                    let value = e.target.value;

                    // Allow only numbers, comma and decimal
                    if (!/^[0-9,]*\.?[0-9]{0,2}$/.test(value)) return;

                    handleChange(index, {
                      target: { name: "monthlyCtc", value }
                    });
                  }}
                  placeholder="e.g., 50,000"
                  className={`form-input width-respo ${errors[`monthlyCtc_${index}`]
                    ? "error"
                    : field.monthlyCtc
                      ? "success"
                      : ""
                    }`}
                />
                {errors[`monthlyCtc_${index}`] && (
                  <span className="error-message font-size-text ">
                    {errors[`monthlyCtc_${index}`]}
                  </span>
                )}
              </div>

              {/* Monthly In Hand */}
              <div className="flex-column width-flex-50">
                <label htmlFor={`monthlyInhand_${index}`} className="form-labels font-weight500    font-size-subheading">
                  <CustomTooltip position="top" tooltipContent={tooltipContent.salary}>
                    Monthly In Hand{inputFields[index].company != "" || inputFields.length > 1 ? <span className="required">*</span> : ""}
                  </CustomTooltip>
                </label>
                <input
                  type="text"
                  maxLength={15}
                  id={`monthlyInhand_${index}`}
                  name="monthlyInhand"
                  value={field.monthlyInhand}
                  // onChange={(e) => handleChange(index, e)}
                  onChange={(e) => {
                    let value = e.target.value;

                    // Allow only numbers, comma and decimal
                    if (!/^[0-9,]*\.?[0-9]{0,2}$/.test(value)) return;

                    handleChange(index, {
                      target: { name: "monthlyInhand", value }
                    });
                  }}
                  placeholder="e.g., 45,000"
                  className={`form-input width-respo ${errors[`monthlyInhand_${index}`]
                    ? "error"
                    : field.monthlyInhand
                      ? "success"
                      : ""
                    }`}
                />
                {errors[`monthlyInhand_${index}`] && (
                  <span className="error-message font-size-text ">
                    {errors[`monthlyInhand_${index}`]}
                  </span>
                )}
              </div>
            </div>

            {/* Overlap Error Message */}
            {errors.overlap_error && index === 0 && (
              <div className="error-message font-size-text">
                <span>{errors.overlap_error}</span>
              </div>
            )}

            <div className="jd-heading-outer">
              <div className="jd-heading-bottom-light"></div>
            </div>
          </>
        ))}

        {/* Add More Button */}
        <div className="jd-heading-outer">
          <div className="jd-heading-outer-flex">
            <button
              type="button"
              onClick={handleAddInputField}
              className="jd-heading-main font-weight600 font-size-text"
            >
              <AddwithBlueCircle />
              {" "} Please click here to add more
            </button>
          </div>
        </div>

        <div className="button-models">
          <button className="model-button model-button-cancel font-weight500" onClick={backroute} disabled={loading}>
            Back
          </button>

          {data ? (
            <button
              onClick={handleFormPut}
              className="model-button font-weight500 model-button-submit"
              disabled={loading}
            >
              {inputFields[0].company !== "" || inputFields.length > 1 ?
                loading ?
                  "Updating..."
                  :
                  "Submit & Proceed"
                :
                "Skip"
              }
            </button>
          ) : (
            <button
              onClick={handleFormSubmit}
              className="model-button font-weight500 model-button-submit"
              disabled={loading}
            >
              {inputFields[0].company !== "" || inputFields.length > 1 ?
                loading ?
                  "Saving..."
                  :
                  "Next"
                :
                "Skip"
              }
            </button>)}
        </div>
      </form>

    </>);
};

const DeletWorkExperience = ({ i, email, getUserDetails }) => {
  const [show, setShow] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = (e) => {
    e.preventDefault();
    setShow(true);
  };

  const [loading, setLoading] = useState(false); //loading logic
  const handleFormSubmitDelete = async (e) => {
    e.preventDefault();
    setLoading(true); //loading logic
    const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic
    try {
      let res = await axios.delete(`${BASE_URL}/userdata/userworkexp/${i.id}/`);

      if (res.status === 200) {
        await getUserDetails(email);
        setShow(false);
      } else {
        console.error(res);
      }
    } catch (err) {
      handleErrorToast(err, loadingToastId)
    } finally {
      setLoading(false); //loading logic
      toast.dismiss(loadingToastId);
    }
  };

  return (
    <>
      <button
        className="model-button model-button-delete"
        title="Delete Work Experience"
        onClick={(e) => handleShow(e)}
      >
        <DeleteDustbin />
      </button>

      <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
        <Modal.Header closeButton>
          <Modal.Title className="align-center">Delete Work Experience</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="parent-div align-center">
            Are You Sure You Want to delete Work Experience - {i.company}
            <div className="button-models">
              <button className="model-button model-button-cancel font-weight500 " onClick={handleClose}>
                Cancel
              </button>
              <button
                onClick={handleFormSubmitDelete}
                className="model-button model-button-delete font-weight500 "
                disabled={loading}
              >
                Delete
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

const DocumentAddBackup = ({ getUserDocs }) => {


  let { email } = useParams()


  const [show, setShow] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const [formData, setFormData] = useState({
    user: email,
    document_name: "",
    document_type: "",
    document_file: "",

  });

  // !  ************** Validation start **************  ! //

  const [errors, setErrors] = useState({});
  const [inputState, setInputState] = useState({});

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = ["document_name", "document_type", "document_file"];
    requiredFields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
          } is required !`;
      }
    });
    console.log(newErrors, 'newerrors', formData)
    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // ?  ************** Validation End **************  ! //
  const [loading, setLoading] = useState(false); //loading logic

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setLoading(true); //loading logic
      const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic

      const formDataa = new FormData();
      formDataa.append("user", formData.user);

      formDataa.append("document_name", formData.document_name);
      formDataa.append("document_type", formData.document_type);



      try {
        const originalFile = formData.document_file;
        if (originalFile) {
          const customFile = generateCustomFileForUpload(originalFile, 'Aimantra');
          formDataa.append("document_file", customFile);
        }
        let res = await axios.post(`${BASE_URL}/userdata/userdocs/`, formDataa, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (res.status === 200) {
          handleClose();
          await getUserDocs();
        } else {
          alert(res);
        }
      } catch (err) {
        handleErrorToast(err, loadingToastId)
      } finally {
        setLoading(false); //loading logic
        toast.dismiss(loadingToastId);

      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setInputState({
      ...inputState,
      [name]: value.trim() ? "green" : "",
    });

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleFileChange = (e) => {
    const { name } = e.target;
    const file = e.target.files[0];

    if (file) {
      if (file.type === "application/pdf") {
        setFormData({
          ...formData,
          [name]: file,
        });
      } else {
        setErrors({
          ...errors,
          [name]: "Invalid file type. Please select a PDF file.",
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: null,
      });
    }
  };

  const handleFileClear = (name) => {
    setFormData({
      ...formData,
      [name]: null,
    });
  };

  return (
    <>
      <div className="upload-svg" onClick={handleShow} >
        <AddwithWhiteCircle />{" "}More Documents
      </div>

      <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
        {/* <Modal.Header closeButton>
          <Modal.Title>Request Leave</Modal.Title>
        </Modal.Header> */}
        <Modal.Body>
          <div className="parent-div">
            <div className="bdy-div">
              <h6 className="task-Tab-heading  font-weight500    font-size-heading">Upload More Document</h6>

              <form>
                <div className="flex-column width-flex-50">
                  <label htmlFor="document_name" className="form-labels font-weight500    announce-date font-weight400  font-size-heading">
                    Document Name
                  </label>
                  <input
                    id="document_name"
                    type="document_name"
                    name="document_name"
                    placeholder="Document Name"
                    onChange={handleInputChange}
                    value={formData.document_name}
                    className={`form-input width-respo ${errors.document_name ? "error" : inputState.document_name ? "success" : ""
                      }`}
                  />
                  {errors.document_name && (
                    <span className="error-message font-size-text ">{errors.document_name}</span>
                  )}
                </div>

                <div className="flex-column width-flex-50">
                  <label htmlFor="document_type" className="form-labels font-weight500    font-size-subheading">
                    Document Type<span className="required">*</span>
                  </label>
                  <select
                    id="document_type"
                    name="document_type"
                    value={formData.document_type}
                    onChange={handleInputChange}
                    className={`form-input width-respo ${errors.document_type ? "error" : inputState.document_type ? "success" : ""
                      }`}
                  >
                    <option value="">Select a Document Type</option>
                    <option value="identity">Identity</option>
                    <option value="previousExperience">Previous Experience</option>
                    <option value="degree">Degree</option>
                  </select>
                  {errors.document_type && (
                    <span className="error-message font-size-text ">{errors.document_type}</span>
                  )}
                </div>



                <div className="flex-column width-flex-50">
                  <label htmlFor="attachment" className="form-labels font-weight500    announce-date font-weight400  font-size-heading">
                    Attachment
                  </label>
                  <input
                    id="file"
                    type="file"
                    name="document_file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    className="file-input"
                  />

                  {formData.document_file ? (
                    <span
                      className="file-clear"
                      onClick={() => handleFileClear("file")}
                    >
                      Clear
                    </span>
                  ) : null}
                </div>
              </form>
              <div className="button-models">
                <button className="model-button model-button-cancel font-weight500" onClick={handleClose}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="model-button model-button-leave font-weight500 font-size-heading"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

const SignatureUploadModal = ({ pic, getUSerDetails, extra_close, addUi = false }) => {

  const [show, setShow] = useState(false);
  const handleClose = () => {
    extra_close(false)
    setShow(false)
  };
  const handleShow = () => setShow(true);

  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);


  const [selectedSignature, setSelectedSignature] = useState(pic?.signature || null)
  const [formDataa, setFormDataa] = useState({
    signature: null,
  });

  const [imagePreview, setImagePreview] = useState(null);

  const userEmail = pic?.email || sessionStorage.getItem('email');

  const uploadFile = async (fileOrImage) => {
    const formDataa = new FormData();

    if (fileOrImage instanceof File) {
      const customFile = generateCustomFileForUpload(fileOrImage, 'Aimantra');
      formDataa.append('signature', customFile);
    }
    const loadingToastId = toast.loading("Loading: Please wait...");
    try {
      let res = await axios.put(`${BASE_URL}/userdata/userdata/${userEmail}/`, formDataa, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.status === 200) {
        await getUSerDetails();
        toast.success("Signature updated successfully!");
        handleClose();
        extra_close(false);

      } else {
        alert(res);
      }
    } catch (err) {
      handleErrorToast(err, loadingToastId);

    } finally {
      setLoading(false);
      toast.dismiss(loadingToastId);
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setSelectedSignature(null);
        setFormDataa({
          ...formDataa,
          signature: file,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileInput = () => {
    document.getElementById('fileInput').click();
  };


  const resetFileInput = () => {
    const fileInput = document.getElementById("fileInput");
    fileInput.value = null;
  };

  const handleSubmit = () => {
    const fileOrImage = formDataa.signature || selectedSignature || pic?.signature;
    uploadFile(fileOrImage);
  };

  // console.log("signature", selectedSignature);

  return (
    <>
      <button
        className="flex items-center gap-1 p-2 rounded-md hover:bg-gray-100 cursor-pointer transition"
        onClick={handleShow}
        type="button"
      >
        {addUi ?
          <div
            style={{
              width: '90px',
              height: '90px',
              borderRadius: '50%',
              backgroundColor: 'rgb(200 223 243)',
              position: 'relative',
              margin: 'auto',
            }}
          >
            <span
              style={{
                fontWeight: '400',
                position: 'absolute',
                top: '-4px',
                fontSize: '68px',
                right: '21px',
                color: '#2576bc',
              }}
            >
              +
            </span>
          </div>
          :
          <>
            <UpdatePencil /><span>Edit</span>
          </>
        }
      </button>
      <Modal show={show} dialogClassName="request-leave width-40vw">

        <Modal.Body>
          <div className="parent-div">
            <div className="bdy-div "
              style={{ backgroundColor: '', justifyContent: 'center', alignItems: 'center' }}

            >
              <h3 className="task-Tab-heading   font-weight500    font-size-heading text-center ">Choose Signature </h3>
              <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '20px', marginBottom: '20px', }}>
                <div
                  style={{
                    width: '90px',
                    height: '90px',
                    borderRadius: '50%',
                    backgroundColor: 'rgb(200 223 243)',
                    position: 'relative',
                    margin: 'auto',
                    // marginTop: '20px', 
                  }}
                >
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Signature"
                      style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        objectFit: 'cover',
                      }}
                    />
                  ) :
                    selectedSignature ? (
                      <img
                        // src={selectedSignaturePic}
                        // src={`https://cipl-aimantra.s3.ap-south-1.amazonaws.com/${selectedSignature}`
                        src={
                          selectedSignature.startsWith("http")
                            ? selectedSignature
                            : `https://cipl-aimantra.s3.ap-south-1.amazonaws.com/${selectedSignature}`
                        }
                        alt="Selected Avatar"
                        style={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '50%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <span
                        onClick={triggerFileInput}
                        style={{
                          cursor: 'pointer',
                          fontWeight: '400',
                          position: 'absolute',
                          top: '-4px',
                          fontSize: '68px',
                          right: '21px',
                          color: '#2576bc',
                        }}
                      >
                        +
                      </span>
                    )
                  }
                </div>
              </div>
              <div style={{ textAlign: 'center', cursor: 'pointer', color: '#2576bc', marginBottom: '10px' }} onClick={triggerFileInput}>Change from Files</div>

              <input
                id="fileInput"
                type="file"
                accept="image/*"
                onChangeCapture={(event) => {
                  handleImageUpload(event);
                  resetFileInput();
                }}
                onChange={handleImageUpload}
                style={{
                  display: 'none',
                }}
              />

              <div className="button-models">
                <button className="model-button   font-weight500    model-button-cancel font-size-heading" onClick={handleClose} disabled={loading}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="model-button   font-weight500    model-button-leave  font-weight500    font-size-heading"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  Upload Signature
                </button>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );


}

const DocumentdetailsBackup = ({ email, id, forwardroutepath, backroutepath, backroutepath2 }) => {



  const [docData, setDocData] = useState([])
  const [buffer, setBuffering] = useState(true); //buffering logic

  const getUserDocs = async () => {
    setBuffering(true); //buffering logic // Start Buffering
    try {
      const ress = await axios.get(`${BASE_URL}/userdata/userdocsfilter/${email}/`);
      setDocData(ress.data);
    } catch (err) {
      // handleErrorToast(err)
    } finally {
      setBuffering(false);// End Buffering
    }
  };

  useEffect(() => {
    getUserDocs();
  }, []);


  const navigatee = useNavigate()


  const backroute = () => {
    backroutepath === "emergencycontacts" ? navigatee(`/${backroutepath}/${email}/`) : navigatee(`/${backroutepath}/${id}/${email}/`);
  }
  const finishSubmit = () => {
    navigatee(`/${backroutepath2}/`)

  }


  const [formData, setFormData] = useState({
    user: email,
    document_name: "",
    document_type: "",
    document_file: "",

  });

  const [loadingAadhar, setLoadingAadhar] = useState(false); //loading logic

  const handleSubmitaadhaar = async (e) => {
    e.preventDefault();


    const formDataa = new FormData();
    formDataa.append("user", email);

    formDataa.append("document_name", "Aadhaar");
    formDataa.append("document_type", "identity");


    if (formData.document_file != '') {
      setLoadingAadhar(true); //loading logic
      const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic
      try {
        const originalFile = formData.document_file;
        if (originalFile) {
          const customFile = generateCustomFileForUpload(originalFile, 'Aimantra');
          formDataa.append("document_file", customFile);
        }
        let res = await axios.post(`${BASE_URL}/userdata/userdocs/`, formDataa, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (res.status === 200) {
          window.location.reload();
          //   handleClose();
        } else {
          alert(res);
        }
      } catch (err) {
        handleErrorToast(err, loadingToastId)
      } finally {
        setLoadingAadhar(false); // Ensure loading state is stopped even in case of error //loading logic
        toast.dismiss(loadingToastId);

      }
    }
    else {
      alert("Please upload a file")
    }
  };



  const [loadingCV, setLoadingCV] = useState(false); //loading logic

  const handleSubmitcv = async (e) => {
    e.preventDefault();



    const formDataa = new FormData();
    formDataa.append("user", email);

    formDataa.append("document_name", "CV");
    formDataa.append("document_type", "identity");




    if (formData.document_file != '') {
      setLoadingCV(true); //loading logic
      const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic

      try {
        const originalFile = formData.document_file;
        if (originalFile) {
          const customFile = generateCustomFileForUpload(originalFile, 'Aimantra');
          formDataa.append("document_file", customFile);
        }
        let res = await axios.post(`${BASE_URL}/userdata/userdocs/`, formDataa, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (res.status === 200) {
          window.location.reload();
          //   handleClose();
        } else {
          alert(res);
        }
      } catch (err) {
        handleErrorToast(err, loadingToastId)
      } finally {
        setLoadingCV(false); // Ensure loading state is stopped even in case of error //loading logic
        toast.dismiss(loadingToastId);
      }
    }
    else {
      alert("Please upload a file")
    }

  };

  const handleFileChange = (e) => {
    const { name } = e.target;
    const file = e.target.files[0];

    setFormData({
      ...formData,
      [name]: file,
    });
  };

  const [ProfilePic, SetProfilePic] = useState({})
  const [jdid, setJdid] = useState()


  const getUSerDetails = async () => {
    setBuffering(true); //buffering logic // Start Buffering
    try {
      const res = await axios.get(`${BASE_URL}/userdata/userdataget/${email}/`);
      SetProfilePic(res.data);
    } catch (err) {
    } finally {
      setBuffering(false);// End Buffering
    }
  };

  const getUserJobDetails = async () => {
    setBuffering(true); //buffering logic // Start Buffering
    try {
      const ress = await axios.get(`${BASE_URL}/jd/jobapplicant/${id}/${email}/`)
      setJdid(ress.data.id)
    } catch (err) {
    } finally {
      setBuffering(false);// End Buffering
    }
  };

  useEffect(() => {
    getUSerDetails();
    getUserJobDetails();
  }, [email, id]);



  const deleteProfilePicture = async () => {
    const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic
    try {
      let res = await axios.put(`${BASE_URL}/userdata/userdata/${email}/`, { profilepic: null });

      if (res.status === 200 || res.status === 201) {
        await getUSerDetails();
        toast.success("Profile picture deleted successfully!");
        setIsOptionShow(false)
      } else {
        console.error("Failed to delete profile picture");
      }
    } catch (err) {
      handleErrorToast(err, loadingToastId)
    } finally {
      toast.dismiss(loadingToastId);
    }
  };

  const deleteSignature = async () => {
    const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic
    try {
      let res = await axios.put(`${BASE_URL}/userdata/userdata/${email}/`, { signature: null });

      if (res.status === 200 || res.status === 201) {
        await getUSerDetails();
        toast.success("Signature deleted successfully!");
        setIsOptionShow(false)
      } else {
        console.error("Failed to delete signature");
      }
    } catch (err) {
      handleErrorToast(err, loadingToastId)
    } finally {
      toast.dismiss(loadingToastId);
    }
  };

  const navigate = useNavigate()

  const handleFormComplete = async (e) => {
    e.preventDefault();
    try {
      navigate(`/processComplete/`)
    } catch (err) {
      alert(err);
    }
  }




  const handleDownload = async (e) => {
    e.preventDefault();
    const isFullUrl = ProfilePic?.profilepic.startsWith('http://') || ProfilePic?.profilepic.startsWith('https://');

    const profilepicUrl = isFullUrl
      // && ProfilePic?.profilepic
      ? ProfilePic.profilepic
      : `https://cipl-aimantra.s3.ap-south-1.amazonaws.com/${ProfilePic.profilepic}`;

    try {
      const response = await fetch(profilepicUrl);
      const blob = await response.blob();

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'profile-pic.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Download failed:", error);
    }

  };

  const handleDownloadSignature = async (e) => {
    e.preventDefault();
    const isFullUrl = ProfilePic?.signature.startsWith('http://') || ProfilePic?.signature.startsWith('https://');

    const signatureUrl = isFullUrl
      ? ProfilePic.signature
      : `https://cipl-aimantra.s3.ap-south-1.amazonaws.com/${ProfilePic.signature}`;

    try {
      const response = await fetch(signatureUrl);
      const blob = await response.blob();

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'signature.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Download failed:", error);
    }

  };


  const handleFormPut = async (e) => {
    e.preventDefault();
    const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic

    try {
      let res = await axios.put(`${BASE_URL}/jd/jobapplicant/${jdid}/`, {
        details_filled: true
      })

      if (res.status === 200) {
        navigate(`/jobApplied/`)
      } else {
        alert(res);
      }
    } catch (err) {
      //toast Logic
      handleErrorToast(err)
    } finally {
      toast.dismiss(loadingToastId);
    }
  }


  const DocDeleteButton = ({ id }) => {
    const handleDocSubmitDelete = async (e) => {
      e.preventDefault();
      const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic

      try {
        let res = await axios.delete(`${BASE_URL}/userdata/userdocs/${id}/`);

        if (res.status === 200) {
          // getDocuments();
          // await getUserDocs();
          window.location.reload();
          // setShow(false);
        } else {
          alert(res);
        }
      } catch (err) {
        //toast Logic
        handleErrorToast(err, loadingToastId)
      } finally {
        toast.dismiss(loadingToastId);
      }
    };
    return (
      <>
        <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
        {buffer ? <Loader container={false} /> :
          <button style={{ marginLeft: '10px' }} title="Delete Doc" onClick={(e) => handleDocSubmitDelete(e)} className="model-delete-button">
            <DeleteDustbin />
          </button>
        }
      </>
    )
  }

  const [isoptionShow, setIsOptionShow] = useState(false);
  const [isSignatureOptionShow, setIsSignatureOptionShow] = useState(false);


  const toggleDisplayOption = () => {
    setIsOptionShow(!isoptionShow);
  };
  const toggleDisplaySignatureOption = () => {
    setIsSignatureOptionShow(!isoptionShow);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {

      if (
        (e.target.closest('.modal-pp-doc') === null && e.target.closest('.dots-container-p-option-doc') === null)
      ) {
        // setIsOptionShow(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <ToastContainer
        position="top-center"
        autoClose={1000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/*  ************** timeline Seaction  ************** */}

      <div className="jd-heading-outer">
        <h2 className="jd-heading-main font-weight600  font-size-heading">Documents Upload*</h2>
        <div>
          <div className="jd-heading-bottom-bold"></div>
          <div className="jd-heading-bottom-light"></div>
        </div>
      </div>

      <form className="register-form">
        <div
          className="flex justify-between"
        >
          <div>

            {docData.find(document => document.document_name === 'Aadhaar') ?
              "" : (
                <div className="flex-column width-flex-50">
                  <label htmlFor="bestAchieve" className="form-labels font-weight500    font-size-subheading">
                    Upload Aadhar<span className="required">*</span>
                  </label>
                  <div className="" key={"index"}>
                    <div className="flex justify-start item-center gap-1">
                      <div className="document-name-div">
                        <img
                          src={Docs}
                          alt="Document Icon"
                          className="document-icon"
                        />
                        <div>
                          <div className="document-name">Aadhaar</div>
                          <input
                            id="file"
                            type="file"
                            name="document_file"
                            accept=".pdf"
                            onChange={handleFileChange}
                            className="file-input"
                          />
                        </div>
                      </div>
                      <button
                        className="document-download-button  width-5vw  width-5vw"
                        onClick={handleSubmitaadhaar}
                        disabled={loadingAadhar}
                      >
                        Upload
                      </button>
                    </div>
                  </div>
                </div>)
            }


            {docData.find(document => document.document_name === 'CV') ? "" :

              (<div className="flex-column width-flex-50">
                <label htmlFor="bestAchieve" className="form-labels font-weight500    font-size-subheading">
                  Upload CV<span className="required">*</span>
                </label>

                <div className="" key={"index"}>
                  <div className="flex justify-start item-center gap-1">
                    <div className="document-name-div">
                      <img
                        src={Docs}
                        alt="Document Icon"
                        className="document-icon"
                      />
                      <div>
                        <div className="document-name">CV</div>
                        <input
                          id="file"
                          type="file"
                          name="document_file"
                          accept=".pdf"
                          onChange={handleFileChange}
                          className="file-input"
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      className="document-download-button  width-5vw  width-5vw"
                      onClick={handleSubmitcv}
                      disabled={loadingCV}
                    >
                      Upload
                    </button>
                  </div>
                </div>
              </div>)
            }
          </div>

          {/* {ProfilePic.profilepic ? */}
          <div className="flex-row">
            <div className="flex-column width-flex-50">
              <label htmlFor="bestAchieve" className="form-labels font-weight500    font-size-subheading">
                Profile Pic
              </label>
              <div className="document-name-div" style={{ position: 'relative', width: 'fit-content', border: '0.5px solid #dfdede' }}>
                <CustomImageModal customStyle>
                  {ProfilePic.profilepic ? (
                    <img
                      src={ProfilePic?.profilepic}
                      alt="Logo Preview"
                      className="document-icons user_img_display"
                    />
                  ) : (
                    <UserDefaultLogo
                      user={{ name: ProfilePic?.name, profilepic: ProfilePic?.profilepic }}
                      customStyleforParent={"document-icons-doc user_img_display"} customStyleforChild={"userDetailsChild_doc"}
                    />
                  )}
                </CustomImageModal>

                <div className="dots-container-p-option-doc dots-container-pp" onClick={toggleDisplayOption}>
                  <TripledotLarge />
                </div>

                {isoptionShow && (
                  <div className="modal-pp-doc modal-profile-modal" style={{ zIndex: '1' }}>
                    <div className="modal-content-p p-2 rounded-lg shadow-lg bg-white border border-gray-200">
                      <div className="flex items-center gap-1 flex-column">
                        <ProfileUploadModal
                          extra_close={() => setIsOptionShow(false)}
                          pic={ProfilePic}
                          getUSerDetails={getUSerDetails}
                        />
                        {ProfilePic.profilepic && (
                          <>
                            <div
                              className="flex items-center gap-1 p-2 rounded-md hover:bg-gray-100 cursor-pointer transition"
                              onClick={handleDownload}
                            >
                              <DownloadIcon className="text-green-500" color={'gray'} />
                              <span className="text-gray-700 font-medium">Download</span>
                            </div>

                            <div
                              className="flex items-center gap-1 p-2 rounded-md hover:bg-gray-100 cursor-pointer transition"
                              onClick={(e) => deleteProfilePicture()}
                            >
                              <DeleteDustbin className="text-red-500" />
                              <span className="text-gray-700 font-medium">Delete</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {" "}
            <div className="flex-column width-flex-50">
              <label htmlFor="bestAchieve" className="form-labels font-weight500    font-size-subheading">
                Signature (Optional)
              </label>
              <div className="document-name-div" style={{ position: 'relative', width: 'fit-content', border: '0.5px solid #dfdede' }}>
                <CustomImageModal customStyle>
                  {ProfilePic.signature ? (
                    <img
                      src={ProfilePic?.signature}
                      alt="Logo Preview"
                      className="document-icons user_img_display"
                    />
                  ) : (
                    <UserDefaultLogo
                      user={{ name: ProfilePic?.name, profilepic: ProfilePic?.signature }}
                      customStyleforParent={"document-icons-doc user_img_display"} customStyleforChild={"userDetailsChild_doc"}
                    />
                  )}
                </CustomImageModal>

                <div className="dots-container-p-option-doc dots-container-pp" onClick={toggleDisplaySignatureOption}>
                  <TripledotLarge />
                </div>

                {isSignatureOptionShow && (
                  <div className="modal-pp-doc modal-profile-modal" style={{ zIndex: '1' }}>
                    <div className="modal-content-p p-2 rounded-lg shadow-lg bg-white border border-gray-200">
                      <div className="flex items-center gap-1 flex-column">
                        <SignatureUploadModal
                          extra_close={() => setIsSignatureOptionShow(false)}
                          pic={ProfilePic}
                          getUSerDetails={getUSerDetails}
                        />
                        {ProfilePic.signature && (
                          <>
                            <div
                              className="flex items-center gap-1 p-2 rounded-md hover:bg-gray-100 cursor-pointer transition"
                              onClick={handleDownloadSignature}
                            >
                              <DownloadIcon className="text-green-500" color={'gray'} />
                              <span className="text-gray-700 font-medium">Download</span>
                            </div>

                            <div
                              className="flex items-center gap-1 p-2 rounded-md hover:bg-gray-100 cursor-pointer transition"
                              onClick={(e) => deleteSignature()}
                            >
                              <DeleteDustbin className="text-red-500" />
                              <span className="text-gray-700 font-medium">Delete</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="jd-heading-outer">
          <div className="jd-bloc-tabs">
            <h2 className="jd-heading-main font-weight600  font-size-heading">Upload Additonal Documents </h2>
            <div className="right-expense-bloc-tab">
            </div>
            <div><DocumentAdd getUserDocs={getUserDocs} /></div>

          </div>
          <div>
            <div className="jd-heading-bottom-bold"></div>
            <div className="jd-heading-bottom-light"></div>

            {docData.length === 0 ? (
              <div className="no-documents-message">
                No documents available.
              </div>
            ) : (
              <div className="document-grid1">
                {docData.map((doc, index) => (
                  (doc.document_name == null) ? "" :
                    <div className="document-item1" key={index}>
                      <div className="document-icons">
                        <div className="document-name-div">
                          <img
                            src={Docs}
                            alt="Document Icon"
                            className="document-icon"
                          />
                          <div>
                            <div className="document-name">{doc.document_name}</div>
                            <div className="document-upload-date font-size-subheading  ">
                              Doc Type <b>{doc.document_type}</b>
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', }}>
                          <a style={{ marginLeft: '10px' }} href={doc.document_file} target="_blank" className="document-download-button  width-5vw  width-5vw">
                            View
                          </a>
                          <DocDeleteButton id={doc.id} />
                        </div>
                      </div>
                    </div>
                ))}
              </div>
            )}
          </div>

        </div>
        <div className="button-models">
          <button className="model-button model-button-cancel font-weight500   " onClick={backroute}>Back</button>

          {(docData.find(document => document.document_name === 'Aadhaar') && docData.find(document => document.document_name === 'CV') && ProfilePic.profilepic)
            ? <button className="model-button  font-weight500    model-button-submit" onClick={backroutepath === "emergencycontacts" ? handleFormComplete : handleFormPut}>
              {backroutepath === "emergencycontacts" ? "Completed" : "Apply"}
            </button>
            : <></>
          }
        </div>
      </form >

    </>
  );
};





const Documentdetails = ({ email, id, forwardroutepath, backroutepath, backroutepath2 }) => {
  const [docData, setDocData] = useState([]);
  const [buffer, setBuffering] = useState(true);
  const [ProfilePic, SetProfilePic] = useState({});
  const [jdid, setJdid] = useState();
  const [isoptionShow, setIsOptionShow] = useState(false);
  const [isSignatureOptionShow, setIsSignatureOptionShow] = useState(false);

  const navigate = useNavigate();

  const getUserDocs = async () => {
    setBuffering(true);
    try {
      const ress = await axios.get(`${BASE_URL}/userdata/userdocsfilter/${email}/`);
      setDocData(ress.data);
    } catch (err) {
      // handleErrorToast(err)
    } finally {
      setBuffering(false);
    }
  };

  const getUSerDetails = async () => {
    setBuffering(true);
    try {
      const res = await axios.get(`${BASE_URL}/userdata/userdataget/${email}/`);
      SetProfilePic(res.data);
    } catch (err) {
    } finally {
      setBuffering(false);
    }
  };

  // const getUserJobDetails = async () => {
  //   setBuffering(true);
  //   try {
  //     const url = id
  //       ? `${BASE_URL}/jd/jobapplicant/${id}/${email}/`
  //       : `${BASE_URL}/jd/jobapplicant/${email}/`;

  //     const ress = await axios.get(url);
  //     // const ress = await axios.get(`${BASE_URL}/jd/jobapplicant/${id}/${email}/`);

  //     setJdid(ress.data.id);
  //   } catch (err) {
  //   } finally {
  //     setBuffering(false);
  //   }
  // };

  const getUserJobDetails = async () => {
    if (!id) return;
    setBuffering(true);
    try {
      const ress = await axios.get(`${BASE_URL}/jd/jobapplicant/${id}/${email}/`);
      setJdid(ress.data.id);
    } catch (err) {
    } finally {
      setBuffering(false);
    }
  };


  useEffect(() => {
    getUserDocs();
    getUSerDetails();
    if (id) {
      getUserJobDetails();
    }

  }, [email, id]);

  const backroute = () => {
    backroutepath === "emergencycontacts"
      ? navigate(`/${backroutepath}/${email}/?company_id=${sessionStorage.getItem("company_id") || "null"}`)
      : navigate(`/${backroutepath}/${id}/${email}/`);
  };

  const finishSubmit = () => {
    navigate(`/${backroutepath2}/`);
  };

  const handleFormComplete = async (e) => {
    e.preventDefault();
    try {
      navigate(`/processComplete/`);
    } catch (err) {
      alert(err);
    }
  };

  const handleFormPut = async (e) => {
    e.preventDefault();
    const loadingToastId = toast.loading("Loading: Please wait...");

    try {
      let res = await axios.put(`${BASE_URL}/jd/jobapplicant/${jdid}/`, {
        details_filled: true
      });

      if (res.status === 200) {
        navigate(`/jobApplied/`);
      } else {
        alert(res);
      }
    } catch (err) {
      handleErrorToast(err);
    } finally {
      toast.dismiss(loadingToastId);
    }
  };

  const deleteProfilePicture = async () => {
    const loadingToastId = toast.loading("Loading: Please wait...");
    try {
      let res = await axios.put(`${BASE_URL}/userdata/userdata/${email}/`, { profilepic: null });

      if (res.status === 200 || res.status === 201) {
        await getUSerDetails();
        toast.success("Profile picture deleted successfully!");
        setIsOptionShow(false);
      }
    } catch (err) {
      handleErrorToast(err, loadingToastId);
    } finally {
      toast.dismiss(loadingToastId);
    }
  };

  const deleteSignature = async () => {
    const loadingToastId = toast.loading("Loading: Please wait...");
    try {
      let res = await axios.put(`${BASE_URL}/userdata/userdata/${email}/`, { signature: null });

      if (res.status === 200 || res.status === 201) {
        await getUSerDetails();
        toast.success("Signature deleted successfully!");
        setIsSignatureOptionShow(false);
      }
    } catch (err) {
      handleErrorToast(err, loadingToastId);
    } finally {
      toast.dismiss(loadingToastId);
    }
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    const isFullUrl = ProfilePic?.profilepic?.startsWith('http://') || ProfilePic?.profilepic?.startsWith('https://');

    const profilepicUrl = isFullUrl
      ? ProfilePic.profilepic
      : `https://cipl-aimantra.s3.ap-south-1.amazonaws.com/${ProfilePic.profilepic}`;

    try {
      const response = await fetch(profilepicUrl);
      const blob = await response.blob();

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'profile-pic.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const handleDownloadSignature = async (e) => {
    e.preventDefault();
    const isFullUrl = ProfilePic?.signature?.startsWith('http://') || ProfilePic?.signature?.startsWith('https://');

    const signatureUrl = isFullUrl
      ? ProfilePic.signature
      : `https://cipl-aimantra.s3.ap-south-1.amazonaws.com/${ProfilePic.signature}`;

    try {
      const response = await fetch(signatureUrl);
      const blob = await response.blob();

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'signature.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const DocDeleteButton = ({ id }) => {
    const handleDocSubmitDelete = async (e) => {
      e.preventDefault();
      const loadingToastId = toast.loading("Loading: Please wait...");

      try {
        let res = await axios.delete(`${BASE_URL}/userdata/userdocs/${id}/`);

        if (res.status === 200) {
          await getUserDocs();
          toast.success("Document deleted successfully!");
        }
      } catch (err) {
        handleErrorToast(err, loadingToastId);
      } finally {
        toast.dismiss(loadingToastId);
      }
    };

    return (
      <button
        title="Delete Document"
        onClick={handleDocSubmitDelete}
        className="doc-delete-btn"
      >
        <DeleteDustbin />
      </button>
    );
  };

  const toggleDisplayOption = () => {
    setIsOptionShow(!isoptionShow);
  };

  const toggleDisplaySignatureOption = () => {
    setIsSignatureOptionShow(!isSignatureOptionShow);
  };

  // Check if required documents are uploaded
  const hasAadhaar = docData.some(doc => doc.document_name === 'Aadhaar');
  const hasCV = docData.some(doc => doc.document_name === 'CV');
  const hasProfilePic = ProfilePic.profilepic;
  const canProceed = hasAadhaar && hasCV && hasProfilePic;

  return (
    <>
      <ToastContainer
        position="top-center"
        autoClose={1000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {buffer ? <Loader /> : null}

      <div className="doc-container">
        {/* Header */}
        <div className="jd-heading-outer">
          <h2 className="jd-heading-main font-weight600 font-size-heading">
            Documents Upload
            <span className="required">*</span>
          </h2>
          <div>
            <div className="jd-heading-bottom-bold"></div>
            <div className="jd-heading-bottom-light"></div>
          </div>
        </div>

        {/* Main Content */}
        <div className="doc-content">
          {/* Required Documents Section */}
          <div className="doc-required-section">
            <h3 className="doc-section-title">Required Documents</h3>

            <div className="doc-required-grid">
              {/* Aadhaar Card Upload */}
              <DocumentUploadCard
                title="Aadhaar Card"
                documentName="Aadhaar"
                isUploaded={hasAadhaar}
                email={email}
                attachment={docData}
                onUploadSuccess={getUserDocs}
                required={true}
              />

              {/* CV/Resume Upload */}
              <DocumentUploadCard
                title="CV / Resume"
                documentName="CV"
                isUploaded={hasCV}
                email={email}
                attachment={docData}
                onUploadSuccess={getUserDocs}
                required={true}
              />

              {/* Profile Picture */}
              <div className="doc-card">
                <div className="doc-card-header">
                  <span className="doc-card-title">Profile Picture</span>
                  {!hasProfilePic && <span className="doc-badge-required">Required</span>}
                </div>

                <div className="doc-profile-container">
                  <div className="doc-profile-wrapper">
                    <CustomImageModal customStyle>
                      {ProfilePic.profilepic ? (
                        <img
                          src={ProfilePic.profilepic}
                          alt="Profile"
                          className="doc-profile-image"
                        />
                      ) : (
                        // <UserDefaultLogo
                        //   user={{ name: ProfilePic?.name }}
                        //   customStyleforParent={"doc-profile-placeholder"}
                        //   customStyleforChild={"doc-profile-placeholder-child"}
                        // />
                        <ProfileUploadModal
                          extra_close={() => setIsOptionShow(false)}
                          pic={ProfilePic}
                          getUSerDetails={getUSerDetails}
                          addUi={true}
                        />
                      )}
                    </CustomImageModal>


                  </div>
                </div>
                <button
                  className="doc-profile-options-btn"
                  onClick={toggleDisplayOption}
                >
                  <TripledotLarge />
                </button>

                {isoptionShow && (
                  <div className="modal-ppp modal-profile-modal">
                    <div className="modal-content-p p-2 rounded-lg bg-white border border-gray-200">
                      <ProfileUploadModal
                        extra_close={() => setIsOptionShow(false)}
                        pic={ProfilePic}
                        getUSerDetails={getUSerDetails}
                      />
                      {ProfilePic.profilepic && (
                        <>
                          <button
                            className="doc-profile-modal-item"
                            onClick={handleDownload}
                          >
                            <DownloadIcon color={'gray'} />
                            <span>Download</span>
                          </button>
                          <button
                            className="doc-profile-modal-item"
                            onClick={deleteProfilePicture}
                          >
                            <DeleteDustbin />
                            <span>Delete</span>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Signature (Optional) */}
              <div className="doc-card">
                <div className="doc-card-header">
                  <span className="doc-card-title">Signature</span>
                  <span className="doc-badge-optional">Optional</span>
                </div>

                <div className="doc-profile-container">
                  <div className="doc-profile-wrapper">
                    <CustomImageModal customStyle>
                      {ProfilePic.signature ? (
                        <img
                          src={ProfilePic.signature}
                          alt="Signature"
                          className="doc-profile-image"
                        />
                      ) : (
                        // <UserDefaultLogo
                        //   user={{ name: ProfilePic?.name }}
                        //   customStyleforParent={"doc-profile-placeholder"}
                        //   customStyleforChild={"doc-profile-placeholder-child"}
                        // />
                        <SignatureUploadModal
                          extra_close={() => setIsSignatureOptionShow(false)}
                          pic={ProfilePic}
                          getUSerDetails={getUSerDetails}
                          addUi={true}
                        />
                      )}
                    </CustomImageModal>


                  </div>
                </div>
                <button
                  className="doc-signature-options-btn"
                  onClick={toggleDisplaySignatureOption}
                >
                  <TripledotLarge />
                </button>

                {isSignatureOptionShow && (
                  <div className="modal-ppp modal-profile-modal">
                    <div className="modal-content-p p-2 rounded-lg bg-white border border-gray-200">
                      <SignatureUploadModal
                        extra_close={() => setIsSignatureOptionShow(false)}
                        pic={ProfilePic}
                        getUSerDetails={getUSerDetails}
                      />
                      {ProfilePic.signature && (
                        <>
                          <button
                            className="doc-profile-modal-item"
                            onClick={handleDownloadSignature}
                          >
                            <DownloadIcon color={'gray'} />
                            <span>Download</span>
                          </button>
                          <button
                            className="doc-profile-modal-item"
                            onClick={deleteSignature}
                          >
                            <DeleteDustbin />
                            <span>Delete</span>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Additional Documents Section */}
          <div className="doc-additional-section">
            <div className="doc-section-header">
              <h3 className="doc-section-title">Additional Documents</h3>
              <DocumentAdd getUserDocs={getUserDocs} />
            </div>

            {docData.filter(doc => doc.document_name !== 'Aadhaar' && doc.document_name !== 'CV').length === 0 ? (
              <div className="doc-empty-message">
                <p>No additional documents uploaded yet.</p>
                <p className="doc-empty-submessage">Click on "More Documents" to upload additional documents.</p>
              </div>
            ) : (
              <div className="doc-grid">
                {docData
                  .filter(doc => doc.document_name !== 'Aadhaar' && doc.document_name !== 'CV')
                  .map((doc, index) => (
                    <div className="doc-item" key={index}>
                      <div className="doc-item-content">
                        <img src={Docs} alt="Document" className="doc-item-icon" />
                        <div className="doc-item-details">
                          <div className="doc-item-name">{doc.document_name}</div>
                          <div className="doc-item-type">{doc.document_type}</div>
                        </div>
                      </div>
                      <div className="doc-item-actions">
                        <a
                          href={doc.document_file}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="doc-view-btn"
                        >
                          View
                        </a>
                        <DocDeleteButton id={doc.id} />
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="doc-modal-actions">
            <button className="model-button  font-weight500    model-button-cancel font-size-heading" onClick={backroute}>
              Back
            </button>

            {canProceed && (
              <button
                className="model-button model-button-leave font-weight500 font-size-heading"
                onClick={backroutepath === "emergencycontacts" ? handleFormComplete : handleFormPut}
              >
                {backroutepath === "emergencycontacts" ? "Complete Registration" : "Apply"}
              </button>
            )}
          </div>

          {!canProceed && (
            <div className="doc-validation-message">
              <p>Please upload all required documents (Aadhaar, CV, and Profile Picture) to proceed.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// DocumentUploadCard Component
const DocumentUploadCard = ({ title, documentName, isUploaded, email, attachment, onUploadSuccess, required = false }) => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png"
    ];

    if (file && allowedTypes.includes(file.type)) {
      setSelectedFile(file);
    } else {
      toast.error("Please select a valid file (PDF, JPG, JPEG, PNG)");
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file to upload");
      return;
    }

    setLoading(true);
    const loadingToastId = toast.loading("Uploading...");

    const formData = new FormData();
    formData.append("user", email);
    formData.append("document_name", documentName);
    formData.append("document_type", "identity");

    try {
      const customFile = generateCustomFileForUpload(selectedFile, 'Aimantra');
      formData.append("document_file", customFile);

      let res = await axios.post(`${BASE_URL}/userdata/userdocs/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.status === 200) {
        toast.success(`${title} uploaded successfully!`);
        setShowModal(false);
        setSelectedFile(null);
        await onUploadSuccess();
      }
    } catch (err) {
      handleErrorToast(err, loadingToastId);
    } finally {
      setLoading(false);
      toast.dismiss(loadingToastId);
    }
  };

  const handleDelete = async () => {
    const docToDelete = attachment.find(doc => doc.document_name === documentName);
    if (!docToDelete) return;

    setDeleteLoading(true);
    const loadingToastId = toast.loading("Deleting...");

    try {
      let res = await axios.delete(`${BASE_URL}/userdata/userdocs/${docToDelete.id}/`);

      if (res.status === 200) {
        toast.success(`${title} deleted successfully!`);
        setShowDeleteConfirm(false);
        await onUploadSuccess();
      }
    } catch (err) {
      handleErrorToast(err, loadingToastId);
    } finally {
      setDeleteLoading(false);
      toast.dismiss(loadingToastId);
    }
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    // Reset file input
    const fileInput = document.getElementById(`file-${documentName}`);
    if (fileInput) fileInput.value = '';
  };

  const getDocumentUrl = () => {
    const doc = attachment.find(doc => doc.document_name === documentName);
    return doc?.document_file;
  };

  // Delete Confirmation Modal
  const DeleteConfirmModal = () => (
    <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} dialogClassName="doc-upload-modal">
      <Modal.Body>
        <div className="doc-upload-content">
          <h3 className="doc-upload-title">Delete {title}</h3>
          <div style={{ textAlign: 'center', margin: '20px 0' }}>
            <p style={{ fontSize: '16px', color: '#333', marginBottom: '10px' }}>
              Are you sure you want to delete this document?
            </p>
            <p style={{ fontSize: '14px', color: '#666' }}>
              This action cannot be undone.
            </p>
          </div>
          <div className="button-models">
            <button
              className="model-button model-button-cancel font-weight500"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={deleteLoading}
            >
              Cancel
            </button>
            <button
              className="model-button model-button-delete font-weight500 font-size-heading"
              onClick={handleDelete}
              disabled={deleteLoading}
            >
              {deleteLoading ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );

  if (isUploaded) {
    return (
      <>
        <div className="doc-card doc-card-uploaded">
          <div className="doc-card-header">
            <span className="doc-card-title">{title}</span>
            <span className="doc-badge-uploaded">Uploaded ✓</span>
          </div>
          <div className="doc-card-content">
            <img src={Docs} alt="Document" className="doc-icon-large" />
            {/* <button className="upload-svg" style={{ padding: '13px 23px' }} onClick={() => window.open(attachment.find(doc => doc.document_name === documentName)?.document_file, '_blank')}>
            View
          </button> */}
            <div className="doc-card-actions" style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button
                className="upload-svg"
                style={{ padding: '8px 20px' }}
                onClick={() => window.open(getDocumentUrl(), '_blank')}
              >
                View
              </button>
              <button
                className="upload-svg"
                style={{ padding: '8px 20px', background: '#dc3545', color: 'white' }}
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
        <DeleteConfirmModal />
      </>
    );
  }

  return (
    <>
      <div className="doc-card">
        <div className="doc-card-header">
          <span className="doc-card-title">{title}</span>
          {required && <span className="doc-badge-required">Required</span>}
        </div>
        <div className="doc-card-content">
          <img src={Docs} alt="Document" className="doc-icon-large" />

          <div className="doc-card-actions" style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <button
              className="upload-svg"
              style={{ padding: '8px 20px' }}
              onClick={() => setShowModal(true)}
            >
              Upload
            </button>
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} dialogClassName="doc-upload-modal">
        <Modal.Body>
          <div className="doc-upload-content">
            <h3 className="doc-upload-title">Upload {title}</h3>

            <div className="doc-upload-area">
              <input
                type="file"
                id={`file-${documentName}`}
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
                className="doc-file-input-hidden"
              />
              <label htmlFor={`file-${documentName}`} className="doc-file-label">
                <svg viewBox="0 0 24 24" width="40" height="40">
                  <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z" />
                </svg>
                <span>Click to browse</span>
                <span className="doc-file-hint">PDF, JPG, PNG files only (max 10MB)</span>
              </label>
              {selectedFile && (
                <div className="doc-selected-file">
                  <span>{selectedFile.name}</span>
                  <button onClick={clearSelectedFile}>×</button>
                </div>
              )}
            </div>

            <div className="button-models">
              <button className="model-button model-button-cancel font-weight500" onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button
                className="model-button model-button-leave font-weight500 font-size-heading"
                onClick={handleUpload}
                disabled={loading || !selectedFile}
              >
                {loading ? "Uploading..." : "Upload"}
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

// Update DocumentAdd component to use unique class names
const DocumentAdd = ({ getUserDocs }) => {
  let { email } = useParams();

  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const [formData, setFormData] = useState({
    user: email,
    document_name: "",
    document_type: "",
    document_file: "",
  });

  const [errors, setErrors] = useState({});
  const [inputState, setInputState] = useState({});
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = ["document_name", "document_type", "document_file"];
    requiredFields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)} is required !`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setLoading(true);
      const loadingToastId = toast.loading("Loading: Please wait...");

      const formDataa = new FormData();
      formDataa.append("user", formData.user);
      formDataa.append("document_name", formData.document_name);
      formDataa.append("document_type", formData.document_type);

      try {
        const originalFile = formData.document_file;
        if (originalFile) {
          const customFile = generateCustomFileForUpload(originalFile, 'Aimantra');
          formDataa.append("document_file", customFile);
        }
        let res = await axios.post(`${BASE_URL}/userdata/userdocs/`, formDataa, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (res.status === 200) {
          handleClose();
          await getUserDocs();
          toast.success("Document uploaded successfully!");
        } else {
          alert(res);
        }
      } catch (err) {
        handleErrorToast(err, loadingToastId);
      } finally {
        setLoading(false);
        toast.dismiss(loadingToastId);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setInputState({
      ...inputState,
      [name]: value.trim() ? "green" : "",
    });

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleFileChange = (e) => {
    const { name } = e.target;
    const file = e.target.files[0];

    if (file) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png"
      ];

      if (allowedTypes.includes(file.type)) {
        setFormData({
          ...formData,
          [name]: file,
        });
      } else {
        toast.error("Please select a valid file (PDF, JPG, JPEG, PNG).");

        setErrors({
          ...errors,
          [name]: "Invalid file type. Please select a  valid file (PDF, JPG, JPEG, PNG).",
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: null,
      });
    }
  };

  const handleFileClear = () => {
    setFormData({
      ...formData,
      document_file: null,
    });
    const fileInput = document.getElementById("file");
    if (fileInput) fileInput.value = '';
  };

  return (
    <>
      <div className="upload-svg" onClick={handleShow}>
        <AddwithWhiteCircle /> More Documents
      </div>

      <Modal show={show} onHide={handleClose} dialogClassName="request-leave">
        <Modal.Body>
          <div className="doc-upload-content">
            <h3 className="doc-upload-title">Upload More Document</h3>

            <form className="">
              <div className="flex-column ">
                <label htmlFor="document_name" className="form-labels font-weight500 font-size-subheading">
                  Document Name<span className="required">*</span>
                </label>
                <input
                  id="document_name"
                  type="text"
                  name="document_name"
                  placeholder="Enter Document Name"
                  onChange={handleInputChange}
                  value={formData.document_name}
                  className={`form-input ${errors.document_name ? "error" : inputState.document_name ? "success" : ""}`}
                />
                {errors.document_name && (
                  <span className="error-message font-size-text">{errors.document_name}</span>
                )}
              </div>

              <div className="flex-column width-flex-50">
                <label htmlFor="document_type" className="form-labels font-weight500 font-size-subheading">
                  Document Type<span className="required">*</span>
                </label>
                <select
                  id="document_type"
                  name="document_type"
                  value={formData.document_type}
                  onChange={handleInputChange}
                  className={`form-input ${errors.document_type ? "error" : inputState.document_type ? "success" : ""}`}
                >
                  <option value="">Select a Document Type</option>
                  <option value="identity">Identity</option>
                  <option value="previousExperience">Previous Experience</option>
                  <option value="degree">Degree</option>
                </select>
                {errors.document_type && (
                  <span className="error-message font-size-text">{errors.document_type}</span>
                )}
              </div>

              <div className="flex-column">
                <label htmlFor="attachment" className="form-labels font-weight500 font-size-subheading">
                  Attachment<span className="required">*</span>
                </label>
                <div className="doc-upload-area" style={{ padding: 0 }}>
                  <input
                    id="file"
                    type="file"
                    name="document_file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    className="doc-file-input-hidden"
                  />
                  <label htmlFor="file" className="doc-file-label" style={{ padding: '15px' }}>
                    <svg viewBox="0 0 24 24" width="30" height="30">
                      <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z" />
                    </svg>
                    <span>Click to browse</span>
                  </label>
                </div>

                {formData.document_file && (
                  <div className="doc-selected-file">
                    <span>{formData.document_file.name}</span>
                    <button onClick={handleFileClear}>×</button>
                  </div>
                )}
                {errors.document_file && (
                  <span className="error-message font-size-text" style={{ margin: 0 }}>{errors.document_file}</span>
                )}
              </div>
            </form>

            <div className="button-models">
              <button className="model-button model-button-cancel font-weight500" onClick={handleClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="model-button model-button-leave font-weight500 font-size-heading"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? "Uploading..." : "Add Document"}
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};


export { PersonaldetailsAdd, EducationdetailsAdd, WorkexperienceAdd, Documentdetails };
